/**
 * Seren theme.js — cart API, cart drawer, accordions, product forms (PDP + spotlight), preloader, scroll reveal.
 */

/* ---- Sticky header shell (layout theme.liquid > .site-header-group) ---- */
(function initSiteHeaderStickyShell() {
  function apply() {
    var shell = document.querySelector('[data-site-header-group]');
    if (!shell) return;
    var on = !!shell.querySelector('[data-header][data-sticky="true"]');
    shell.classList.toggle('site-header-group--sticky', on);
  }
  apply();
  document.addEventListener('shopify:section:load', apply);
})();

/* Promo bars (announcement, urgency) live in `promo-group` above the sticky
   header in normal document flow. They scroll away naturally and only re-appear
   when the user scrolls back to the top — same model as any other page content.
   No JS scroll handler required. */

/* -------------------------------------------------------------------------- */
/* Toast notification — single global instance with severity tones             */
/* -------------------------------------------------------------------------- */
/* API:
     SerenToast.show('msg')                              // info, 3500ms
     SerenToast.show('msg', 5000)                        // legacy: 2nd arg = duration
     SerenToast.show('msg', { tone: 'success' })         // success, 3500ms
     SerenToast.show('msg', { tone: 'error', duration: 6000 })
     SerenToast.success('msg' [, durationOrOpts])        // helper
     SerenToast.error('msg' [, durationOrOpts])
     SerenToast.info('msg' [, durationOrOpts])
     SerenToast.cartAddError() / .cartUpdateError()      // existing helpers
     SerenToast.dismiss()                                // hide immediately

   The 2nd-arg-is-number form is kept for backwards compatibility with
   existing call sites that pre-date the tone system; they default to
   tone "info" but new error callers should pass { tone: 'error' }.
*/

(function () {
  var el = null;
  var msgEl = null;
  var timer = null;
  var DEFAULT_DURATION = 3500;
  var VALID_TONES = ['info', 'success', 'error'];

  function getEl() {
    if (!el) {
      el = document.querySelector('[data-seren-toast]');
      if (el) {
        msgEl = el.querySelector('[data-seren-toast-message]');
        var closeBtn = el.querySelector('[data-seren-toast-close]');
        if (closeBtn) closeBtn.addEventListener('click', dismiss);
      }
    }
    return el;
  }

  function dismiss() {
    var toast = getEl();
    if (!toast) return;
    toast.classList.remove('is-visible');
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function show(msg, optsOrDuration) {
    var toast = getEl();
    if (!toast || !msgEl) return;

    /* Normalize: 2nd arg can be a number (legacy duration) or an
       options object. Anything else falls back to defaults. */
    var opts;
    if (typeof optsOrDuration === 'number') {
      opts = { duration: optsOrDuration };
    } else if (optsOrDuration && typeof optsOrDuration === 'object') {
      opts = optsOrDuration;
    } else {
      opts = {};
    }

    var tone = VALID_TONES.indexOf(opts.tone) !== -1 ? opts.tone : 'info';
    var duration = typeof opts.duration === 'number' && opts.duration > 0
      ? opts.duration
      : DEFAULT_DURATION;

    /* Setting textContent (vs innerHTML) avoids any HTML injection from
       merchant-supplied locale strings or call-site interpolation. Clear
       first then set on the next frame so aria-live re-announces even when
       the same message string fires twice in a row (e.g. retrying a failing
       add-to-cart) — some screen readers skip a "no change" assignment. */
    msgEl.textContent = '';
    toast.setAttribute('data-tone', tone);
    toast.classList.add('is-visible');

    requestAnimationFrame(function () {
      msgEl.textContent = msg;
    });

    if (timer) clearTimeout(timer);
    timer = setTimeout(function () {
      toast.classList.remove('is-visible');
      timer = null;
    }, duration);
  }

  /* Parse #theme-strings once (lazily, on first lookup — theme.js is
     deferred, so the blob at the end of <body> exists by then) and
     expose the lookup as window.t(key, fallback) for the rest of the
     theme. A failed parse caches {} so every call falls back. */
  var themeStrings = null;
  function t(key, fallback) {
    if (themeStrings === null) {
      var s = document.getElementById('theme-strings');
      if (!s) return fallback || '';
      try { themeStrings = JSON.parse(s.textContent) || {}; } catch (e) { themeStrings = {}; }
    }
    return themeStrings[key] || fallback || '';
  }
  window.t = t;

  function showCartAddError() {
    show(t('cart_error_add', 'Could not add to cart. Please try again.'), { tone: 'error' });
  }

  function showCartUpdateError() {
    show(t('cart_error_update', 'Something went wrong. Please try again.'), { tone: 'error' });
  }

  window.SerenToast = {
    show: show,
    success: function (msg, optsOrDuration) { return show(msg, normalizeWithTone(optsOrDuration, 'success')); },
    error:   function (msg, optsOrDuration) { return show(msg, normalizeWithTone(optsOrDuration, 'error')); },
    info:    function (msg, optsOrDuration) { return show(msg, normalizeWithTone(optsOrDuration, 'info')); },
    dismiss: dismiss,
    cartAddError: showCartAddError,
    cartUpdateError: showCartUpdateError
  };

  /* Helpers fold a tone into either a legacy numeric duration or an
     existing opts object. `success(msg, 5000)` and
     `success(msg, { duration: 5000 })` both work. */
  function normalizeWithTone(optsOrDuration, tone) {
    if (typeof optsOrDuration === 'number') return { tone: tone, duration: optsOrDuration };
    if (optsOrDuration && typeof optsOrDuration === 'object') {
      return { tone: tone, duration: optsOrDuration.duration };
    }
    return { tone: tone };
  }
})();

/* -------------------------------------------------------------------------- */
/* Cart drawer + centralized cart API (Phase 0 Step 0.9)                        */
/* -------------------------------------------------------------------------- */

(function () {
  var SEREN_DRAWER_CLOSE_FALLBACK_MS = 500;

  function dispatchCartUpdated(cart) {
    // Dispatch BOTH the theme-prefixed event (internal contract) and the
    // unprefixed `cart:updated` event (cross-app convention pioneered by
    // Dawn). Cart-targeting apps — Klaviyo abandoned-cart hooks, free-
    // shipping bars, upsell apps that re-render after a mutation — listen
    // for `cart:updated` by convention. Dispatching both keeps internal
    // listeners working AND gains broad app compatibility.
    var detail = { cart: cart };
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: detail }));
    document.dispatchEvent(new CustomEvent('theme:cart:updated', { detail: detail }));
  }

  var cartUpsellResizeTimer;
  /** Set by initCartDrawer; used after section HTML replace to reopen without stealing focus. */
  var reopenCartDrawerAfterSectionReplace = null;
  /** Tracks the document-level drawer-toggle click listener so section replaces can swap it
   *  without leaking prior closures (one per section replace would otherwise accumulate). */
  var drawerToggleDocClickHandler = null;

  /** Ref-counted scroll lock: multiple drawers/overlays can request a lock without
   * fighting each other. `is-scroll-locked` only releases when every requester unlocks. */
  var scrollLockCount = 0;
  function lockScroll() {
    if (scrollLockCount === 0) {
      document.documentElement.classList.add('is-scroll-locked');
    }
    scrollLockCount++;
  }
  function unlockScroll() {
    if (scrollLockCount <= 0) return;
    scrollLockCount--;
    if (scrollLockCount === 0) {
      document.documentElement.classList.remove('is-scroll-locked');
    }
  }

  /** Safari (iOS + macOS) stops compositing the sticky header's ::before
   *  backdrop-filter glass while the page is scroll-locked for an overlay, and
   *  only RE-SAMPLES it when the backdrop behind it moves — i.e. on scroll. So
   *  after an overlay closes, the scrolled header reads FULLY TRANSPARENT (its
   *  glass tint is ~2% — the look is all blur) until the user scrolls. Replicate
   *  that scroll: nudge 1px and back so Safari re-composites the glass at once.
   *  Net-zero + imperceptible; no-op unless the header is in its scrolled (glass)
   *  state. A backdrop-filter property toggle does NOT work here — only moving
   *  the sampled backdrop forces the re-sample. */
  function recompositeHeaderGlass() {
    var header = document.querySelector('.header');
    if (!header || !header.classList.contains('header--scrolled')) return;
    var y = window.scrollY || window.pageYOffset || 0;
    if (y <= 0) return;
    var maxY = document.documentElement.scrollHeight - window.innerHeight;
    var nudge = y >= maxY - 1 ? y - 1 : y + 1;
    window.scrollTo(0, nudge);
    requestAnimationFrame(function () {
      window.scrollTo(0, y);
    });
  }

  /** One observer catches is-scroll-locked being REMOVED by ANY overlay — the
   *  cart drawer (via unlockScroll above), the header menu + predictive search
   *  (header.liquid toggles the class directly, NOT through unlockScroll),
   *  community-stories, etc. — so the glass recomposite isn't tied to a single
   *  close path. Runs on the falling edge only. */
  if ('MutationObserver' in window) {
    (function () {
      var html = document.documentElement;
      var wasLocked = html.classList.contains('is-scroll-locked');
      new MutationObserver(function () {
        var locked = html.classList.contains('is-scroll-locked');
        if (wasLocked && !locked) requestAnimationFrame(recompositeHeaderGlass);
        wasLocked = locked;
      }).observe(html, { attributes: true, attributeFilter: ['class'] });
    })();
  }
  window.SerenScrollLock = { lock: lockScroll, unlock: unlockScroll };

  /** Modal keyboard helpers shared by the cart drawer (below) and the header
   *  menu drawer (sections/header.liquid). One Tab-wrap handler instead of a
   *  copy per overlay.
   *  - focusable(container, extraSelector?) → tabbable descendants, in DOM order
   *  - trapTab(event, focusables) → wraps Tab / Shift+Tab between first and last
   *  - inertOutside(keep) → marks every sibling of `keep` and of each of its
   *    ancestors (up to <body>) inert, so the page behind an open overlay drops
   *    out of the tab order and the accessibility tree; returns a restore
   *    function that un-inerts exactly the nodes it changed (anything already
   *    inert — a closed drawer, the search overlay — stays inert). */
  var FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function focusableIn(container, extraSelector) {
    if (!container) return [];
    var selector = extraSelector ? FOCUSABLE_SELECTOR + ', ' + extraSelector : FOCUSABLE_SELECTOR;
    return Array.prototype.slice.call(container.querySelectorAll(selector));
  }

  function trapTabKey(e, focusables) {
    if (e.key !== 'Tab' || !focusables || focusables.length === 0) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  var INERT_SKIP_TAGS = /^(SCRIPT|STYLE|LINK|META|TEMPLATE|NOSCRIPT)$/;
  function inertOutside(keep) {
    var changed = [];
    var node = keep;
    while (node && node !== document.body && node.parentElement) {
      var siblings = node.parentElement.children;
      for (var i = 0; i < siblings.length; i++) {
        var sib = siblings[i];
        if (sib === node || sib.inert || INERT_SKIP_TAGS.test(sib.tagName)) continue;
        sib.inert = true;
        changed.push(sib);
      }
      node = node.parentElement;
    }
    return function restoreInert() {
      changed.forEach(function (el) {
        el.inert = false;
      });
      changed = [];
    };
  }

  window.SerenFocusTrap = { focusable: focusableIn, trapTab: trapTabKey, inertOutside: inertOutside };

  /** Dark-section overlap watcher.
   *  Single rAF-coalesced scroll loop shared across all consumers (header, sticky ATC,
   *  any future sticky/floating bars). Each registered element gets a class toggled when
   *  its vertical midpoint overlaps a section whose scheme declares `--scheme-is-dark: 1`.
   *
   *  Why mid-point trigger (not IntersectionObserver): mid-point recomputed each frame
   *  stays correct when the sticky ATC bar shows/hides, the iOS address bar resizes,
   *  or the device rotates. Pre-computed rootMargins drift in those cases.
   *
   *  Why --scheme-is-dark (not a scheme name list): the token already lives on every scheme
   *  in sections.css §2.9. Reading it makes detection scheme-agnostic — any new dark scheme
   *  variant added to the design system is auto-detected with zero JS changes.
   *
   *  Usage:
   *    SerenDarkOverlap.observe(barEl, { className: 'header--on-dark' });
   *    SerenDarkOverlap.observe(barEl, { callback: function (onDark) { ... } });
   *    SerenDarkOverlap.unobserve(barEl);
   */
  window.SerenDarkOverlap = (function () {
    var observers = [];
    var darkSections = [];
    var ticking = false;
    var initialized = false;

    function isDarkScheme(section) {
      return window.getComputedStyle(section).getPropertyValue('--scheme-is-dark').trim() === '1';
    }

    function refresh() {
      /* Match any direct child of .shopify-section that carries a color-scheme class —
         not just <section> tags. The footer wraps with <div class="footer__shell ...
         color-scheme-X">, and other sections may use varied tags (header, footer, div).
         The narrower `> section` selector used previously missed those entirely. */
      var all = document.querySelectorAll('.shopify-section > [class*="color-scheme-"]');
      var next = [];
      for (var i = 0; i < all.length; i++) {
        if (isDarkScheme(all[i])) next.push(all[i]);
      }
      darkSections = next;
    }

    function check() {
      ticking = false;
      if (!observers.length) return;
      for (var i = 0; i < observers.length; i++) {
        var obs = observers[i];
        var rect = obs.el.getBoundingClientRect();
        var triggerY = rect.top + rect.height * 0.5;
        var onDark = false;
        for (var j = 0; j < darkSections.length; j++) {
          var r = darkSections[j].getBoundingClientRect();
          if (r.top <= triggerY && r.bottom > triggerY) { onDark = true; break; }
        }
        if (onDark !== obs.lastState) {
          obs.lastState = onDark;
          if (obs.className) obs.el.classList.toggle(obs.className, onDark);
          if (obs.callback) obs.callback(onDark);
        }
      }
    }

    function schedule() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(check);
    }

    function onSectionChange() {
      refresh();
      schedule();
    }

    /** Lazy init — only attach global listeners once the first observer registers. */
    function ensureInit() {
      if (initialized) return;
      initialized = true;
      refresh();
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      document.addEventListener('shopify:section:load', onSectionChange);
      document.addEventListener('shopify:section:unload', onSectionChange);
    }

    return {
      observe: function (el, opts) {
        if (!el) return;
        opts = opts || {};
        ensureInit();
        observers.push({
          el: el,
          className: opts.className || null,
          callback: opts.callback || null,
          lastState: null
        });
        schedule();
      },
      unobserve: function (el) {
        observers = observers.filter(function (o) { return o.el !== el; });
      },
      refresh: refresh
    };
  })();

  /** Debounce resize handler for upsell carousel snap + sticky foot (coarse; not tied to CSS ms tokens in JS). */
  var CART_UPSELL_RESIZE_DEBOUNCE_MS = 120;
  /** Second pass after open / silent restore so layout + sticky foot settle after paint. */
  var CART_DRAWER_LAYOUT_SETTLE_MS = 520;
  var CART_DRAWER_BODY_SCROLL_TOLERANCE_PX = 4;

  /** Pin upsell to bottom of cart body (flex margin) when body does not scroll; else in-flow after line items. */
  function syncCartDrawerUpsellStickyFoot(root) {
    if (!root) return;
    var body = root.querySelector('.cart-drawer__body');
    var upsell = root.querySelector('.cart-drawer__upsell');
    if (!body || !upsell) return;
    var tolerance = CART_DRAWER_BODY_SCROLL_TOLERANCE_PX;
    var overflow = body.scrollHeight > body.clientHeight + tolerance;
    upsell.classList.toggle('cart-drawer__upsell--sticky-foot', !overflow);
  }

  function scheduleSyncCartDrawerUpsellStickyFoot(root) {
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        syncCartDrawerUpsellStickyFoot(root);
      });
    });
  }

  function prefersReducedMotion() {
    return (
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function scheduleCartUpsellCarouselResize() {
    if (cartUpsellResizeTimer) window.clearTimeout(cartUpsellResizeTimer);
    cartUpsellResizeTimer = window.setTimeout(function () {
      cartUpsellResizeTimer = null;
      document
        .querySelectorAll('[data-cart-upsell-carousel][data-cart-upsell-inited]')
        .forEach(function (wrap) {
          var viewport = wrap.querySelector('[data-cart-upsell-viewport]');
          if (!viewport) return;
          var slides = viewport.querySelectorAll('.cart-drawer__upsell-slide');
          if (!slides.length) return;
          var scroll = viewport.scrollLeft;
          var bestIdx = 0;
          var bestDist = Infinity;
          var i;
          for (i = 0; i < slides.length; i++) {
            var t = slides[i].offsetLeft;
            var d = Math.abs(scroll - t);
            if (d < bestDist) {
              bestDist = d;
              bestIdx = i;
            }
          }
          if (slides[bestIdx]) viewport.scrollLeft = slides[bestIdx].offsetLeft;
        });
      var cartDrawerRoot = document.querySelector('[data-cart-drawer]');
      if (cartDrawerRoot) syncCartDrawerUpsellStickyFoot(cartDrawerRoot);
    }, CART_UPSELL_RESIZE_DEBOUNCE_MS);
  }

  window.addEventListener('resize', scheduleCartUpsellCarouselResize, { passive: true });

  function initCartDrawerUpsellCarousels(root) {
    if (!root) return;
    var carousels = root.querySelectorAll('[data-cart-upsell-carousel]');
    carousels.forEach(function (wrap) {
      if (wrap.getAttribute('data-cart-upsell-inited') === 'true') return;

      var viewport = wrap.querySelector('[data-cart-upsell-viewport]');
      var dots = wrap.querySelectorAll('[data-cart-upsell-dot]');

      if (!viewport) return;

      var slides = viewport.querySelectorAll('.cart-drawer__upsell-slide');
      if (!slides.length) return;

      wrap.setAttribute('data-cart-upsell-inited', 'true');

      function maxIdx() {
        return slides.length - 1;
      }

      function scrollTargetForIndex(idx) {
        var el = slides[idx];
        return el ? el.offsetLeft : 0;
      }

      function currentIndex() {
        var scroll = viewport.scrollLeft;
        var bestIdx = 0;
        var bestDist = Infinity;
        var i;
        for (i = 0; i < slides.length; i++) {
          var t = scrollTargetForIndex(i);
          var d = Math.abs(scroll - t);
          if (d < bestDist) {
            bestDist = d;
            bestIdx = i;
          }
        }
        return bestIdx;
      }

      function goTo(i) {
        var clamped = Math.min(maxIdx(), Math.max(0, i));
        var left = scrollTargetForIndex(clamped);
        var instant = prefersReducedMotion();
        viewport.scrollTo({
          left: left,
          behavior: instant ? 'auto' : 'smooth',
        });
      }

      function syncUi() {
        var idx = currentIndex();
        dots.forEach(function (dot) {
          var di = parseInt(dot.getAttribute('data-cart-upsell-dot-index'), 10);
          if (window.isNaN(di)) return;
          if (di === idx) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }

      var scrollRaf = null;
      function onViewportScroll() {
        if (scrollRaf) window.cancelAnimationFrame(scrollRaf);
        scrollRaf = window.requestAnimationFrame(function () {
          scrollRaf = null;
          syncUi();
        });
      }

      viewport.addEventListener('scroll', onViewportScroll, { passive: true });

      dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          var di = parseInt(dot.getAttribute('data-cart-upsell-dot-index'), 10);
          if (!window.isNaN(di)) goTo(di);
        });
      });

      viewport.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          goTo(currentIndex() - 1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          goTo(currentIndex() + 1);
        }
      });

      // Desktop drag-to-scroll — click-and-drag the upsell slider with a mouse.
      // Gated to fine pointers + multi-slide, so touch devices keep their native
      // swipe and a single-slide carousel gets no grab affordance. Pointer events
      // only (no new requests); dots + keyboard still work if this never runs.
      if (slides.length > 1 && window.matchMedia && window.matchMedia('(pointer: fine)').matches) {
        viewport.classList.add('is-draggable');
        var dragging = false;
        var dragStartX = 0;
        var dragStartScroll = 0;
        var dragMoved = false;

        viewport.addEventListener('pointerdown', function (e) {
          if (e.button !== 0) return;
          dragging = true;
          dragMoved = false;
          dragStartX = e.clientX;
          dragStartScroll = viewport.scrollLeft;
          viewport.classList.add('is-dragging');
          // Pointer capture is DEFERRED to the first real move (pointermove below).
          // Capturing here retargets the subsequent click to the viewport, so the
          // delegated upsell "Add" handler sees e.target = viewport (not the button)
          // and silently no-ops. A pure click must never capture.
        });

        viewport.addEventListener('pointermove', function (e) {
          if (!dragging) return;
          var dx = e.clientX - dragStartX;
          if (Math.abs(dx) > 3 && !dragMoved) {
            dragMoved = true;
            // Now it's a real drag — capture so moves keep tracking outside the viewport.
            if (viewport.setPointerCapture) {
              try {
                viewport.setPointerCapture(e.pointerId);
              } catch (err) {}
            }
          }
          viewport.scrollLeft = dragStartScroll - dx;
        });

        function endUpsellDrag() {
          if (!dragging) return;
          dragging = false;
          viewport.classList.remove('is-dragging');
          // Settle on the nearest slide after a real drag.
          if (dragMoved) goTo(currentIndex());
        }

        viewport.addEventListener('pointerup', endUpsellDrag);
        viewport.addEventListener('pointercancel', endUpsellDrag);

        // A drag ends with a click on the slide — suppress it (capture phase, so
        // it beats the link) so dragging past a product doesn't open it.
        viewport.addEventListener(
          'click',
          function (e) {
            if (dragMoved) {
              e.preventDefault();
              e.stopPropagation();
              dragMoved = false;
            }
          },
          true,
        );
      }

      syncUi();
    });
  }

  /** Upsell carousel reconcile across drawer re-renders. The drawer section is
   *  replaced wholesale on every cart change, which used to snap the carousel
   *  back to slide 1. Remember which product the shopper was looking at (and
   *  its index) before the swap; afterwards land on the same product if it is
   *  still offered, else the same position (clamped) — instantly, no smooth
   *  scroll. The scroll listener then re-syncs the dots. */
  function readCartUpsellPosition(drawer) {
    var viewport = drawer && drawer.querySelector('[data-cart-upsell-viewport]');
    if (!viewport) return null;
    var slides = viewport.querySelectorAll('.cart-drawer__upsell-slide');
    if (!slides.length || viewport.scrollLeft <= 0) return null;
    var best = 0;
    var bestDist = Infinity;
    for (var i = 0; i < slides.length; i++) {
      var d = Math.abs(viewport.scrollLeft - slides[i].offsetLeft);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    return { pid: slides[best].getAttribute('data-upsell-product-id'), index: best };
  }

  function restoreCartUpsellPosition(drawer, state) {
    if (!state || !drawer) return;
    var viewport = drawer.querySelector('[data-cart-upsell-viewport]');
    if (!viewport) return;
    var slides = viewport.querySelectorAll('.cart-drawer__upsell-slide');
    if (!slides.length) return;
    var target = null;
    for (var i = 0; i < slides.length; i++) {
      if (state.pid && slides[i].getAttribute('data-upsell-product-id') === state.pid) {
        target = slides[i];
        break;
      }
    }
    if (!target) target = slides[Math.min(state.index, slides.length - 1)];
    viewport.scrollTo({ left: target.offsetLeft, behavior: 'auto' });
  }

  /** Full `.shopify-section` replace for the drawer (vs innerHTML swap) so closure-captured
   *  panel/backdrop refs in initCartDrawer get rebound, and the open state is silently restored.
   *  Captures scroll position of the scrolling body before the swap and restores it after, so
   *  qty/remove operations in long carts don't yank the user back to the top. */
  function applyCartDrawerSectionHtml(html) {
    if (typeof html !== 'string' || !html.trim()) return;
    var current = document.querySelector('[data-cart-drawer]');
    if (!current) return;
    var wasOpen = current.classList.contains('is-open');
    var oldBody = current.querySelector('.cart-drawer__body');
    var savedScroll = oldBody ? oldBody.scrollTop : 0;
    var upsellState = readCartUpsellPosition(current);
    if (wasOpen) unlockScroll();
    if (current._serenDrawerKeydown) {
      document.removeEventListener('keydown', current._serenDrawerKeydown);
      current._serenDrawerKeydown = null;
    }
    var host = current.closest('.shopify-section');
    if (!host) return;
    var wrap = document.createElement('div');
    wrap.innerHTML = html.trim();
    var next = wrap.querySelector('.shopify-section');
    if (!next) return;
    host.replaceWith(next);
    initCartDrawer();
    if (savedScroll > 0) {
      var newBody = document.querySelector('[data-cart-drawer] .cart-drawer__body');
      if (newBody) newBody.scrollTop = savedScroll;
    }
    restoreCartUpsellPosition(document.querySelector('[data-cart-drawer]'), upsellState);
    if (wasOpen && typeof reopenCartDrawerAfterSectionReplace === 'function') {
      reopenCartDrawerAfterSectionReplace();
    }
    /* The drawer section was fully replaced (replaceWith), so any listeners an
       app bound directly to the old drawer node are now orphaned, and no
       shopify:section:load fires on a cart mutation. Dispatch a re-init hook so
       cart-drawer apps (loyalty, upsell, analytics) can re-bind to the new DOM. */
    document.dispatchEvent(
      new CustomEvent('theme:cart-drawer:replaced', {
        bubbles: true,
        detail: { drawer: document.querySelector('[data-cart-drawer]') },
      })
    );
  }

  function initCartDrawer() {
    var root = document.querySelector('[data-cart-drawer]');
    if (!root || root.getAttribute('data-cart-drawer-init') === 'true') return;
    root.setAttribute('data-cart-drawer-init', 'true');

    var panel = root.querySelector('.cart-drawer__panel');
    var backdrop = root.querySelector('[data-cart-drawer-backdrop]');
    var lastFocus = null;
    var closeCleanupTimer = null;
    var panelTransitionEndHandler = null;
    var closePending = false;

    function getFocusable() {
      return focusableIn(panel);
    }

    function clearCloseAnimation() {
      if (closeCleanupTimer) {
        clearTimeout(closeCleanupTimer);
        closeCleanupTimer = null;
      }
      if (panel && panelTransitionEndHandler) {
        panel.removeEventListener('transitionend', panelTransitionEndHandler);
        panelTransitionEndHandler = null;
      }
    }

    function finishDrawerClose() {
      if (!closePending) return;
      closePending = false;
      clearCloseAnimation();
      // Move focus OUT of the drawer BEFORE marking it hidden. Chrome flags
      // an aria-hidden ancestor of a focused element ("Blocked aria-hidden
      // on an element because its descendant retained focus"). The close
      // button typically has focus from the click that triggered close, so
      // we have to handle this even when lastFocus is unset (blur first).
      //
      // Also verify lastFocus is still in the DOM — a section refresh
      // while the drawer was open can detach the original trigger (e.g.
      // a header re-render swaps out the cart-icon button). Calling
      // .focus() on a detached node silently fails AND leaves focus on
      // the inert drawer descendant — exactly what we're trying to avoid.
      if (
        lastFocus &&
        typeof lastFocus.focus === 'function' &&
        document.contains(lastFocus)
      ) {
        lastFocus.focus();
      } else if (
        document.activeElement &&
        root.contains(document.activeElement) &&
        typeof document.activeElement.blur === 'function'
      ) {
        document.activeElement.blur();
      }
      root.setAttribute('aria-hidden', 'true');
      // `inert` is the modern equivalent — forces focus out and removes
      // descendants from the tab order at the platform level. Belt-and-
      // suspenders alongside aria-hidden for browsers that support it
      // (every evergreen, Safari 15.5+).
      root.inert = true;
      unlockScroll();
      if (root._serenDrawerKeydown) {
        document.removeEventListener('keydown', root._serenDrawerKeydown);
        root._serenDrawerKeydown = null;
      } else {
        document.removeEventListener('keydown', onDrawerKeydown);
      }
      lastFocus = null;
    }

    function onDrawerKeydown(e) {
      if (!root.classList.contains('is-open')) return;
      /* A quick-shop dialog opened from a drawer upsell sits on top (top
         layer); Escape / Tab belong to it, not to the drawer underneath. */
      var qsDialog = document.querySelector('[data-quick-shop-modal]');
      if (qsDialog && qsDialog.open) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        closeDrawer();
        return;
      }

      if (e.key !== 'Tab' || !panel) return;

      trapTabKey(e, getFocusable());
    }

    function openDrawer(trigger, opts) {
      opts = opts || {};
      if (root.classList.contains('is-open')) return;

      clearCloseAnimation();
      closePending = false;
      if (!opts.skipFocus) {
        lastFocus =
          trigger && typeof trigger.focus === 'function' ? trigger : document.activeElement;
      }

      root.setAttribute('aria-hidden', 'false');
      root.inert = false;
      root.classList.add('is-open');
      /* Line entrance stagger (CSS, cart-drawer.liquid) only on a shopper-
         initiated open — the silent reopen after a cart re-render skips it. */
      root.classList.add('is-entering');
      setTimeout(function () {
        root.classList.remove('is-entering');
      }, 600);
      lockScroll();
      document.addEventListener('keydown', onDrawerKeydown);
      root._serenDrawerKeydown = onDrawerKeydown;

      // Internal `theme:drawer:open` for our own listeners + the
      // unprefixed `cart:open` for app convention compatibility (apps
      // that mount UI in the cart drawer listen for this to know when
      // to render).
      window.dispatchEvent(new CustomEvent('cart:open'));
      window.dispatchEvent(
        new CustomEvent('theme:drawer:open', {
          detail: { name: 'cart' },
        })
      );

      // Force a cart-discount re-eval so the drawer reflects what the
      // customer will actually pay at checkout (handles the case where
      // a discount was archived/deleted while the customer's cart had
      // cached allocations from when it was active).
      refreshCartDiscounts();

      scheduleSyncCartDrawerUpsellStickyFoot(root);
      window.setTimeout(function () {
        scheduleSyncCartDrawerUpsellStickyFoot(root);
      }, CART_DRAWER_LAYOUT_SETTLE_MS);

      if (!opts.skipFocus) {
        requestAnimationFrame(function () {
          var focusables = Array.prototype.slice.call(getFocusable());
          var target = focusables.length ? focusables[0] : panel;
          if (target && typeof target.focus === 'function') {
            target.focus();
          }
        });
      }
    }

    /** After cart API + section HTML swap: keep drawer open without re-animating or refocusing. */
    function restoreDrawerOpenSilent() {
      if (root.classList.contains('is-open')) return;
      clearCloseAnimation();
      closePending = false;
      root.classList.add('cart-drawer--suppress-transition');
      root.setAttribute('aria-hidden', 'false');
      root.inert = false;
      root.classList.add('is-open');
      lockScroll();
      document.addEventListener('keydown', onDrawerKeydown);
      root._serenDrawerKeydown = onDrawerKeydown;
      scheduleSyncCartDrawerUpsellStickyFoot(root);
      window.setTimeout(function () {
        scheduleSyncCartDrawerUpsellStickyFoot(root);
      }, CART_DRAWER_LAYOUT_SETTLE_MS);
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          root.classList.remove('cart-drawer--suppress-transition');
        });
      });
    }

    reopenCartDrawerAfterSectionReplace = function () {
      restoreDrawerOpenSilent();
    };

    function closeDrawer() {
      if (!root.classList.contains('is-open')) return;

      clearCloseAnimation();
      closePending = true;
      root.classList.remove('is-open');

      function onPanelTransitionEnd(e) {
        if (!panel || e.target !== panel) return;
        if (e.propertyName !== 'transform') return;
        finishDrawerClose();
      }

      if (panel) {
        panelTransitionEndHandler = onPanelTransitionEnd;
        panel.addEventListener('transitionend', panelTransitionEndHandler);
      }

      closeCleanupTimer = window.setTimeout(finishDrawerClose, SEREN_DRAWER_CLOSE_FALLBACK_MS);
    }

    if (drawerToggleDocClickHandler) {
      document.removeEventListener('click', drawerToggleDocClickHandler);
    }
    drawerToggleDocClickHandler = function (e) {
      var toggle = e.target.closest('[data-cart-drawer-toggle]');
      if (!toggle) return;
      e.preventDefault();
      if (root.classList.contains('is-open')) {
        closeDrawer();
      } else {
        openDrawer(toggle);
      }
    };
    document.addEventListener('click', drawerToggleDocClickHandler);

    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-cart-drawer-close]')) {
        closeDrawer();
      }
    });

    if (backdrop) {
      backdrop.addEventListener('click', function () {
        closeDrawer();
      });
    }

    initCartDrawerUpsellCarousels(root);
    scheduleSyncCartDrawerUpsellStickyFoot(root);

    var cartBody = root.querySelector('.cart-drawer__body');
    if (cartBody && typeof ResizeObserver !== 'undefined') {
      var cartDrawerBodyRo = new ResizeObserver(function () {
        syncCartDrawerUpsellStickyFoot(root);
      });
      cartDrawerBodyRo.observe(cartBody);
    }

    window.openCartDrawer = function (trigger, opts) {
      openDrawer(trigger, opts);
    };
  }

  function getCartRefreshSectionIds() {
    var out = [];
    var seen = {};
    document.querySelectorAll('[data-cart-section-refresh]').forEach(function (el) {
      var id = el.getAttribute('data-cart-section-refresh');
      if (id && !seen[id]) {
        seen[id] = true;
        out.push(id);
      }
    });
    return out;
  }

  function applyCartRefreshSections(sections) {
    if (!sections || typeof sections !== 'object') return;
    Object.keys(sections).forEach(function (id) {
      var html = sections[id];
      if (typeof html !== 'string' || !html.trim()) return;
      var current = document.querySelector('[data-cart-section-refresh="' + id + '"]');
      if (!current) return;
      if (current.hasAttribute('data-cart-drawer')) {
        applyCartDrawerSectionHtml(html);
        return;
      }
      var tpl = document.createElement('div');
      tpl.innerHTML = html.trim();
      var next = tpl.querySelector('[data-cart-section-refresh="' + id + '"]');
      if (!next) {
        // A standard Section Rendering response wraps the content in the
        // [data-cart-section-refresh] element. If an app returns the inner
        // markup without that wrapper, don't silently keep stale content —
        // warn (so it's debuggable) and apply the raw response for that id.
        if (window.console && console.warn) {
          console.warn('[SerenCart] section "' + id + '" response had no [data-cart-section-refresh] wrapper; applied raw response.');
        }
        current.innerHTML = tpl.innerHTML;
        return;
      }
      current.innerHTML = next.innerHTML;
    });
  }

  /** Force Shopify to re-evaluate discounts against the customer's cart
   *  by making an empty cart write, then re-fetch the cart sections so
   *  rendered prices reflect the fresh allocations.
   *
   *  Why: discount allocations are written to cart line items at
   *  cart-write time (add/update/remove) and cached. If a discount is
   *  deleted in admin (archived bundle, expired code), existing carts
   *  keep their stale `line_level_discount_allocations` — drawer + cart
   *  page show the discount, but checkout re-evaluates and doesn't apply
   *  it. Customer sees $X in cart, pays $Y. Calling /cart/update.js with
   *  an empty body counts as a write → Shopify re-evaluates → allocations
   *  match what checkout will use.
   *
   *  Cost: one extra POST + Section Rendering API fetch per cart drawer
   *  open. ~100ms total. Fire-and-forget (fail-silent) so a hiccup never
   *  blocks the drawer from opening. */
  // Single-flight guard — rapid drawer toggles or simultaneous addToCart
  // + manual open would otherwise fire multiple POSTs in flight, with
  // last-resolved winning (potentially clobbering a fresher render with
  // an older response). Returning the in-flight promise lets all callers
  // share one round-trip.
  var refreshCartDiscountsInFlight = null;
  function refreshCartDiscounts() {
    if (typeof fetch !== 'function') return Promise.resolve();
    if (refreshCartDiscountsInFlight) return refreshCartDiscountsInFlight;
    // Skip on empty carts — no allocations to re-evaluate, the round-trip
    // accomplishes nothing. Reads the count from the cart-drawer marker
    // attribute (always present even when the drawer is closed/hidden).
    var state = readCartStateFromDom();
    if (state.item_count === 0) return Promise.resolve();
    refreshCartDiscountsInFlight = fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    })
      .then(function (r) {
        // Bail on POST failure (rate limit, validation, 5xx). Re-fetching
        // sections wouldn't show fresher allocations — Shopify hasn't
        // re-evaluated discounts. Avoids a wasted section render.
        if (!r || !r.ok) return null;
        var ids = getCartRefreshSectionIds();
        if (ids.length === 0) return null;
        return fetch('/?sections=' + ids.join(','));
      })
      .then(function (r) {
        if (!r || !r.ok) return null;
        return r.json();
      })
      .then(function (sections) {
        if (sections) applyCartRefreshSections(sections);
      })
      .catch(function () { /* swallow — best effort */ })
      .then(function () {
        refreshCartDiscountsInFlight = null;
      });
    return refreshCartDiscountsInFlight;
  }
  // Expose so the cart page (sections/cart.liquid) can trigger the same
  // re-eval on its own page load. The cart page doesn't go through the
  // openDrawer hook, so it'd otherwise show stale discount allocations
  // until the customer interacts with the cart.
  window.refreshCartDiscounts = refreshCartDiscounts;

  /** Reads the current cart's item_count from the cart-drawer marker attribute
   *  set in cart-drawer.liquid. Used by addItems to skip a redundant GET /cart.js
   *  after add.js has already returned the freshly-rendered drawer HTML. Returns
   *  a minimal cart-shaped object so consumers (header badge, etc.) keep their
   *  existing detail.cart.item_count contract. */
  function readCartStateFromDom() {
    var marker = document.querySelector('[data-cart-drawer][data-cart-item-count]');
    var raw = marker ? marker.getAttribute('data-cart-item-count') : null;
    var n = parseInt(raw, 10);
    return { item_count: isNaN(n) ? 0 : n };
  }

  /** Per-line debounce state for SerenCart.scheduleQuantityCommit. Map keyed by
   *  cart line key — multiple rapid +/- clicks on the same line collapse into
   *  one network request, while concurrent edits to different lines still race
   *  in parallel as expected. */
  var pendingQtyCommits = new Map();
  var CART_QTY_DEBOUNCE_MS = 280;

  /** `opts.skipSections`: note updates fire while the textarea is focused — a DOM swap
   *  would steal focus mid-typing. */
  function cartMutationFetch(url, payload, opts) {
    var sectionIds = (opts && opts.skipSections) ? [] : getCartRefreshSectionIds();
    var body = Object.assign({}, payload);
    if (sectionIds.length) body.sections = sectionIds;
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (res) {
      if (!res.ok) {
        return res.json().then(function (err) {
          throw err;
        });
      }
      return res.json();
    });
  }

  window.SerenCart = {
    addToCart: function (variantId, quantity) {
      quantity = quantity || 1;
      return window.SerenCart.addItems([{ id: variantId, quantity: quantity }]);
    },

    addItems: function (items) {
      if (!items || !items.length) {
        return Promise.resolve(null);
      }
      /* /cart/add.js returns the added items shape, not the full cart. Previously we
         followed up with a GET /cart.js solely so we could dispatch
         theme:cart:updated with cart.item_count for the header badge — doubling the
         latency of every add. The cart-drawer section we requested in the same POST
         already includes the new item_count rendered into [data-cart-item-count] on
         the drawer root, so we read it from the swapped DOM and skip the round trip. */
      return cartMutationFetch('/cart/add.js', { items: items }).then(function (addResponse) {
        var sections = addResponse && addResponse.sections;
        applyCartRefreshSections(sections);
        var cart = readCartStateFromDom();
        dispatchCartUpdated(cart);
        return cart;
      });
    },

    updateQuantity: function (lineKey, quantity) {
      return cartMutationFetch('/cart/change.js', { id: lineKey, quantity: quantity }).then(function (cart) {
        // When this change shifts a bundle's gift entitlement, let the bundle sync
        // render ONCE with the settled result instead of flashing the un-reconciled
        // state (old gifts at the new quantity) and correcting it a beat later.
        if (!vbTryCoalesceRender(cart)) {
          applyCartRefreshSections(cart && cart.sections);
        }
        dispatchCartUpdated(cart);
        return cart;
      });
    },

    removeItem: function (lineKey) {
      return window.SerenCart.updateQuantity(lineKey, 0);
    },

    updateNote: function (note) {
      return cartMutationFetch('/cart/update.js', { note: note || '' }, { skipSections: true }).then(function (cart) {
        dispatchCartUpdated(cart);
        return cart;
      });
    },

    /* Persist cart-level attributes (affiliate/UTM passthrough, gift wrap,
       delivery date, etc). Mirrors updateNote — same /cart/update.js endpoint,
       skips section re-render. Pass a flat { key: value } object. */
    updateAttributes: function (attributes) {
      return cartMutationFetch('/cart/update.js', { attributes: attributes || {} }, { skipSections: true }).then(function (cart) {
        dispatchCartUpdated(cart);
        return cart;
      });
    },

    /** Optimistic +/- step handler shared by cart drawer and cart page. Reads
     *  current qty from the input, applies delta (clamped 0-999), updates the
     *  input value immediately so the UI reads as instant, then schedules a
     *  debounced commit. Pass an element inside [data-cart-line-key] as
     *  originEl. Returns void; no-ops if there's nothing to do. */
    stepLineQuantity: function (originEl, delta) {
      var row = originEl && originEl.closest('[data-cart-line-key]');
      if (!row) return;
      var key = row.getAttribute('data-cart-line-key');
      var input = row.querySelector('[data-cart-qty-input]');
      if (!key || !input) return;
      var current = parseInt(input.value, 10);
      if (isNaN(current)) current = 0;
      var next = current + delta;
      if (next < 0) next = 0;
      if (next > 999) next = 999;
      if (next === current) return;
      input.value = String(next);
      window.SerenCart.scheduleQuantityCommit(key, next);
    },

    /** Debounced quantity commit. Multiple calls for the same line within
     *  CART_QTY_DEBOUNCE_MS collapse into a single /cart/change.js request with
     *  the latest qty — so holding +/- doesn't queue N round-trips. Pass qty=0
     *  to remove. Consumers should optimistically update the input value BEFORE
     *  calling so the UI feels instant; on failure, the input is restored from
     *  its data-committed-qty attribute (server-rendered truth) and a toast
     *  fires. Fire-and-forget; returns no promise. Most consumers should call
     *  stepLineQuantity() instead of this directly. */
    scheduleQuantityCommit: function (key, qty) {
      if (!key) return;
      var existing = pendingQtyCommits.get(key);
      if (existing) clearTimeout(existing);
      var timer = setTimeout(function () {
        pendingQtyCommits.delete(key);
        var p = qty === 0
          ? window.SerenCart.removeItem(key)
          : window.SerenCart.updateQuantity(key, qty);
        p.catch(function () {
          var input = document.querySelector(
            '[data-cart-line-key="' + key + '"] [data-cart-qty-input]'
          );
          if (input) {
            var committed = input.getAttribute('data-committed-qty');
            if (committed != null && committed !== '') input.value = committed;
          }
          if (window.SerenToast) window.SerenToast.cartUpdateError();
        });
      }, CART_QTY_DEBOUNCE_MS);
      pendingQtyCommits.set(key, timer);
    },
  };

  initCartDrawer();

  document.addEventListener('shopify:section:load', function (ev) {
    if (ev.target && ev.target.querySelector('[data-cart-drawer]')) {
      initCartDrawer();
    }
  });

  /* ============================================================
   * Volume-bundle free-gift SYNC (cart drawer)
   * Each PDP add is its own UNIT — a parent line tagged _bundle_unit, with its
   * gift lines carrying the same unit. Gifts are slaved per unit: a pure function
   * of THAT parent line's own quantity (+ variant). On any cart change we sync each
   * unit — ADD its gifts that aren't in the cart (via /cart/add.js, tagged
   * _free_gift + _bundle_unit), REMOVE/REDUCE any beyond its entitlement, and
   * REMOVE orphaned gifts whose parent unit is gone (/cart/update.js) — then a brief
   * (3s) note explains it. So bumping a unit's qty adds gifts, dropping it removes
   * them, removing the parent removes all of its gifts. Entitlement is non-cumulative
   * cycling past the top tier (vbEntitlement). Inputs come from
   * [data-seren-vbundle-reconcile] (rendered server-side). Mirrors run.ts.
   * ============================================================ */
  var vbGiftReconciling = false;
  var vbReconcilePending = false; // a cart change arrived while a sync was in flight

  function vbNumId(gid) {
    if (gid == null) return '';
    var m = String(gid).match(/(\d+)(?!.*\d)/);
    return m ? m[1] : String(gid);
  }

  // The set of variants a SHOPPER-PICKABLE gift may resolve to, as numeric ids,
  // or null for a plain (pinned/default) gift. Reads either island shape: the
  // cart-reconcile island emits a flat `eligibleVariantIds`; the per-variant
  // (giftsByVariant) island passes `eligibleVariants` objects through wholesale.
  // When set, ANY cart line whose variant is in this set satisfies the gift's
  // entitlement — so the shopper's pick is honored instead of being reconciled
  // back to a single config variant.
  function vbEligibleVids(g) {
    var raw = null;
    if (g && g.eligibleVariantIds && g.eligibleVariantIds.length) raw = g.eligibleVariantIds;
    else if (g && g.eligibleVariants && g.eligibleVariants.length)
      raw = g.eligibleVariants.map(function (ev) { return ev && ev.variantId; });
    if (!raw) return null;
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      var n = vbNumId(raw[i]);
      if (n) out.push(n);
    }
    return out.length ? out : null;
  }

  function vbReachedTier(cfg, mainQty) {
    var reached = null;
    (cfg.tiers || []).forEach(function (t) {
      var q = parseInt(t.quantity, 10) || 0;
      if (mainQty >= q && (!reached || q > (parseInt(reached.quantity, 10) || 0))) reached = t;
    });
    return reached;
  }

  // A tier's gift set for the main variant: per-variant entry when present, else
  // the default gifts.
  function vbTierGifts(cfg, t, mainVid) {
    if (cfg.giftsVaryByVariant && t.giftsByVariant && mainVid != null) {
      var own = t.giftsByVariant['gid://shopify/ProductVariant/' + vbNumId(mainVid)];
      if (own && own.length) return own;
    }
    return t.gifts || [];
  }

  // Strict override + inherit-when-empty: the single highest tier at-or-below the
  // reached tier that has gifts ([] when none).
  function vbQualifiedGifts(cfg, reached, mainVid) {
    if (!reached) return [];
    var tiers = (cfg.tiers || []).slice().sort(function (a, b) {
      return (parseInt(b.quantity, 10) || 0) - (parseInt(a.quantity, 10) || 0);
    });
    for (var i = 0; i < tiers.length; i++) {
      if ((parseInt(tiers[i].quantity, 10) || 0) > (parseInt(reached.quantity, 10) || 0)) continue;
      var set = vbTierGifts(cfg, tiers[i], mainVid);
      if (set && set.length) return set;
    }
    return [];
  }

  // Non-cumulative cycling entitlement for ONE parent line: completed top-tier
  // cycles × the top set + the partial remainder's set (today's strict "highest
  // tier reached" rule). For qty up to the top tier this equals a single tier's
  // gifts, so existing single-bundle carts are unchanged. Returns gift entries
  // with their TOTAL quantity summed across cycles. Mirrors run.ts.
  function vbEntitlement(cfg, qty, vid) {
    qty = parseInt(qty, 10) || 0;
    if (qty <= 0) return [];
    var tiers = cfg.tiers || [];
    if (!tiers.length) return [];
    var topQty = 0;
    tiers.forEach(function (t) {
      var q = parseInt(t.quantity, 10) || 0;
      if (q > topQty) topQty = q;
    });
    if (topQty <= 0) return [];
    var cycles = Math.floor(qty / topQty);
    var rem = qty - cycles * topQty;
    var totals = {}; // numericVid -> { variantId, quantity, title, available }
    function accrue(set, mult) {
      if (mult <= 0) return;
      (set || []).forEach(function (g) {
        if (!g || !g.variantId) return;
        var k = vbNumId(g.variantId);
        var add = (parseInt(g.quantity, 10) || 1) * mult;
        if (!totals[k])
          totals[k] = {
            variantId: g.variantId,
            quantity: 0,
            title: g.title || '',
            available: g.available,
            eligibleVids: vbEligibleVids(g),
          };
        totals[k].quantity += add;
      });
    }
    if (cycles > 0) accrue(vbQualifiedGifts(cfg, vbReachedTier(cfg, topQty), vid), cycles);
    if (rem > 0) accrue(vbQualifiedGifts(cfg, vbReachedTier(cfg, rem), vid), 1);
    var out = [];
    Object.keys(totals).forEach(function (k) {
      if (totals[k].quantity > 0) out.push(totals[k]);
    });
    return out;
  }

  function vbReadReconcileData() {
    var el = document.querySelector('[data-seren-vbundle-reconcile]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // Sync each bundle's free-gift lines to its parent's current tier: ADD the gifts
  // the tier grants that aren't in the cart yet, REMOVE/REDUCE any beyond the tier's
  // entitlement, and REMOVE orphaned gift lines whose parent was removed. Gifts are
  // a pure function of the parent's tier — bumping the parent qty adds, dropping it
  // removes. Returns { adds: [{ id, quantity, properties }], updates: { key: qty },
  // added: [titles], removed: [titles] } or null when already in sync.
  function vbBuildPlan(data) {
    if (!data || !data.configs || !data.lines) return null;
    var byId = {};
    data.configs.forEach(function (c) { if (c && c.bundleId) byId[c.bundleId] = c; });
    var adds = [];
    var updates = {};
    var added = [];
    var removed = [];
    var any = false;

    // Current gift lines grouped by their parent UNIT (_bundle_unit), then by gift
    // variant. Each PDP add is its own unit, so a unit's gifts are independent of
    // other units of the same bundle.
    var curByUnit = {}; // unit -> { numericVid -> [lines] }
    var parentUnits = {}; // units that still have a live, configured parent line
    data.lines.forEach(function (l) {
      if (!l.gift) return;
      var u = l.unit || '';
      if (!curByUnit[u]) curByUnit[u] = {};
      var v = vbNumId(l.vid);
      if (!curByUnit[u][v]) curByUnit[u][v] = [];
      curByUnit[u][v].push(l);
    });

    // 1. Each untagged parent line is ONE bundle unit → sync its gifts to its own
    //    entitlement (cycling), keyed by the line's _bundle_unit. The parent is the
    //    line whose product carries the volume metafield (l.bundleMain), marked
    //    server-side since the display metafield has no targets.
    data.lines.forEach(function (l) {
      if (l.gift) return;            // a gift line, not a parent
      if (!l.bundleMain) return;     // not a bundle parent
      var cfg = byId[l.bundleMain];
      if (!cfg) return;
      var unit = l.unit || '';
      if (!unit) return;             // pre-unit add — can't slave gifts per unit
      // A variant the operator excluded from the bundle isn't a checkout target —
      // the discount Function never entitles (zeroes) its gift, so adding one here
      // would charge the shopper for a "free" gift. Leave the unit out of
      // parentUnits (step 2 then removes any stray gift line on it) and grant
      // nothing. Mirrors run.ts's target match + the PDP widget's variant gate.
      // Per-product allowed list (keyed by numeric product id; values are variant
      // GIDs), so a narrowed product gates its excluded variants even when another
      // product in a multi-product bundle is unnarrowed. Falls back to the legacy
      // flat list for bundles published before per-product gating.
      var allowedList = null;
      if (cfg.allowedVariantIdsByProduct && l.pid != null) {
        allowedList = cfg.allowedVariantIdsByProduct[String(l.pid)] || null;
      }
      if (!allowedList && cfg.allowedVariantIds && cfg.allowedVariantIds.length) {
        allowedList = cfg.allowedVariantIds;
      }
      if (allowedList && allowedList.length) {
        var allowed = false;
        for (var ai = 0; ai < allowedList.length; ai++) {
          if (vbNumId(allowedList[ai]) === vbNumId(l.vid)) { allowed = true; break; }
        }
        if (!allowed) return;
      }
      parentUnits[unit] = true;

      var gset = vbEntitlement(cfg, l.qty, l.vid);
      // Split the entitlement: PINNED gifts (one fixed variant — today's path,
      // left byte-identical) vs PICKABLE gifts (a curated eligible set the
      // shopper chose from — ANY in-set variant satisfies it). With no pickable
      // gifts, `groups` stays empty and `claimed` stays empty, so plain/existing
      // bundles reconcile exactly as before.
      var targetByVid = {}; // numericVid -> { qty, title, available }   (pinned)
      var groups = []; // [{ defaultVid, want, title, available, set:[numericVids] }]
      gset.forEach(function (g) {
        if (!g || !g.variantId) return;
        var elig = g.eligibleVids;
        if (elig && elig.length) {
          groups.push({
            defaultVid: vbNumId(g.variantId),
            want: parseInt(g.quantity, 10) || 0,
            title: g.title || '',
            available: g.available,
            set: elig,
          });
        } else {
          targetByVid[vbNumId(g.variantId)] = {
            qty: parseInt(g.quantity, 10) || 0,
            title: g.title || '',
            available: g.available,
          };
        }
      });
      var cur = curByUnit[unit] || {};
      // Numeric vids a pickable group owns in this unit — the pinned REMOVE pass
      // must NOT zero them (they're the shopper's valid in-set pick, reconciled
      // by their group, not "an unexpected variant").
      var claimed = {};
      // PICKABLE groups: the entitlement is a TOTAL free quantity across the
      // eligible set, not one specific variant. Count in-set lines, top up with
      // the default (first available eligible) when short, trim ANY in-set line
      // when over. The shopper's chosen in-set variants are preserved.
      groups.forEach(function (grp) {
        var grpLines = [];
        var have = 0;
        for (var si = 0; si < grp.set.length; si++) {
          var v = grp.set[si];
          if (claimed[v]) continue; // an earlier group already owns this vid
          claimed[v] = true;
          (cur[v] || []).forEach(function (gl) { grpLines.push(gl); have += gl.qty; });
        }
        if (have < grp.want && grp.available !== false) {
          adds.push({ id: parseInt(grp.defaultVid, 10), quantity: grp.want - have, properties: { _free_gift: l.bundleMain, _bundle_unit: unit } });
          added.push(grp.title);
          any = true;
        } else if (have > grp.want) {
          var gexcess = have - grp.want;
          for (var gj = grpLines.length - 1; gj >= 0 && gexcess > 0; gj--) {
            var ggl = grpLines[gj];
            var gkeep = ggl.qty - gexcess;
            if (gkeep < 0) gkeep = 0;
            if (gkeep !== ggl.qty) {
              updates[ggl.key] = gkeep;
              gexcess -= ggl.qty - gkeep;
              removed.push(ggl.title);
              any = true;
            }
          }
        }
      });
      // ADD: pinned variants short of their entitlement (tagged to THIS unit).
      Object.keys(targetByVid).forEach(function (vid) {
        var want = targetByVid[vid].qty;
        var have = 0;
        (cur[vid] || []).forEach(function (gl) { have += gl.qty; });
        if (have < want && targetByVid[vid].available !== false) {
          adds.push({ id: parseInt(vid, 10), quantity: want - have, properties: { _free_gift: l.bundleMain, _bundle_unit: unit } });
          added.push(targetByVid[vid].title);
          any = true;
        }
      });
      // REMOVE / REDUCE: this unit's gift variants over the entitlement (0 if not
      // in the set). Aggregate per variant so a gift split across lines is trimmed
      // by its TOTAL. Skip vids a pickable group owns (reconciled above).
      Object.keys(cur).forEach(function (vid) {
        if (claimed[vid]) return;
        var want = targetByVid[vid] ? targetByVid[vid].qty : 0;
        var lines = cur[vid];
        var have = 0;
        for (var i = 0; i < lines.length; i++) have += lines[i].qty;
        var excess = have - want;
        for (var j = lines.length - 1; j >= 0 && excess > 0; j--) {
          var gl = lines[j];
          var keep = gl.qty - excess;
          if (keep < 0) keep = 0;
          if (keep !== gl.qty) {
            updates[gl.key] = keep; // reduce, or 0 = remove
            excess -= gl.qty - keep;
            removed.push(gl.title);
            any = true;
          }
        }
      });
    });

    // 2. Orphaned gift lines — their unit has no live/configured parent (parent
    //    removed, config gone, or a pre-unit gift) → remove.
    data.lines.forEach(function (l) {
      if (!l.gift) return;
      if (parentUnits[l.unit || '']) return; // handled per-unit in step 1
      if (updates[l.key] !== undefined) return;
      updates[l.key] = 0;
      removed.push(l.title);
      any = true;
    });

    return any ? { adds: adds, updates: updates, added: added, removed: removed } : null;
  }

  function vbNoticeText(plan) {
    if (plan.added.length && !plan.removed.length) {
      return plan.added.length === 1 && plan.added[0]
        ? 'Free gift added: ' + plan.added[0]
        : 'Free gifts added to your bundle';
    }
    return 'Free gifts updated for your quantity';
  }

  function vbShowNotice(text) {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    var host = drawer.querySelector('.cart-drawer__body') || drawer;
    var prev = host.querySelector('.cart-drawer__gift-notice');
    if (prev && prev.parentNode) prev.parentNode.removeChild(prev);
    var note = document.createElement('div');
    note.className = 'cart-drawer__gift-notice';
    note.setAttribute('role', 'status');
    note.setAttribute('aria-live', 'polite');
    note.textContent = text;
    host.insertBefore(note, host.firstChild);
    void note.offsetWidth; // reflow so the slide-in transition runs
    note.classList.add('is-visible');
    window.setTimeout(function () {
      note.classList.remove('is-visible');
      window.setTimeout(function () {
        if (note.parentNode) note.parentNode.removeChild(note);
      }, 320);
    }, 3000);
  }

  function reconcileBundleGifts() {
    // A cart change that lands mid-sync is remembered and drained when the sync
    // finishes — otherwise its event (and the sync's own trailing one) is guarded
    // out and the bidirectional add/remove is left half-applied until the next click.
    if (vbGiftReconciling) { vbReconcilePending = true; return; }
    var plan = vbBuildPlan(vbReadReconcileData());
    if (!plan) return;
    vbGiftReconciling = true;
    var text = vbNoticeText(plan);
    // Optimistically collapse the lines we're about to remove so the cart doesn't
    // linger at full length during the round-trip (it re-renders a beat later). Only
    // fully-removed lines (qty 0); remember them so we can restore on failure.
    var hidden = [];
    Object.keys(plan.updates).forEach(function (key) {
      if (plan.updates[key] !== 0) return;
      var el = document.querySelector('[data-cart-line-key="' + key + '"]');
      var li = el && el.closest ? el.closest('.cart-drawer__line') : null;
      if (li) { li.style.display = 'none'; hidden.push(li); }
    });
    // Removals/reductions via /cart/update.js, then additions via /cart/add.js. The
    // last response carries the re-rendered drawer sections. The plan is idempotent
    // (empty once gifts match the tier), so this never loops.
    var chain = Promise.resolve(null);
    if (Object.keys(plan.updates).length) {
      chain = chain.then(function () {
        return cartMutationFetch('/cart/update.js', { updates: plan.updates });
      });
    }
    if (plan.adds.length) {
      chain = chain.then(function () {
        return cartMutationFetch('/cart/add.js', { items: plan.adds });
      });
    }
    chain
      .then(function (cart) {
        applyCartRefreshSections(cart && cart.sections);
        dispatchCartUpdated(cart);
        vbShowNotice(text);
      })
      .catch(function () {
        // Sync failed — restore the optimistically-hidden lines so the drawer
        // matches the (untouched) cart.
        hidden.forEach(function (li) { li.style.display = ''; });
      })
      .then(function () {
        vbGiftReconciling = false;
        if (vbReconcilePending) { vbReconcilePending = false; reconcileBundleGifts(); }
      });
  }

  // Parse the reconcile island out of a cart Section Rendering response (the
  // freshly-rendered drawer HTML) WITHOUT touching the DOM — so a cart mutation
  // can compute its gift changes against the new cart and apply them before the
  // first paint.
  function vbParseReconcileFromSections(sections) {
    if (!sections || typeof sections !== 'object') return null;
    var keys = Object.keys(sections);
    for (var i = 0; i < keys.length; i++) {
      var html = sections[keys[i]];
      if (typeof html !== 'string' || html.indexOf('data-seren-vbundle-reconcile') === -1) continue;
      var wrap = document.createElement('div');
      wrap.innerHTML = html;
      var el = wrap.querySelector('[data-seren-vbundle-reconcile]');
      if (!el) continue;
      try { return JSON.parse(el.textContent); } catch (e) { return null; }
    }
    return null;
  }

  // Coalesce a cart mutation + its gift reconcile into a SINGLE drawer render.
  // Returns true when it takes over rendering (gift changes are pending): it
  // applies the add/remove against the FRESH cart and renders once with the
  // settled result, so the drawer never flashes the un-reconciled state (old
  // gifts at the new quantity) and then corrects it a beat later. Returns false —
  // caller renders normally — when there are no gift changes, a sync is already
  // running, or anything goes wrong (the add/remove then falls back to the normal
  // event-driven reconcile).
  function vbTryCoalesceRender(cart) {
    if (vbGiftReconciling) return false;
    var plan;
    try {
      var data = vbParseReconcileFromSections(cart && cart.sections);
      if (!data) return false;
      plan = vbBuildPlan(data);
    } catch (e) {
      return false;
    }
    if (!plan) return false;
    vbGiftReconciling = true;
    var text = vbNoticeText(plan);
    var chain = Promise.resolve(cart);
    if (Object.keys(plan.updates).length) {
      chain = chain.then(function () {
        return cartMutationFetch('/cart/update.js', { updates: plan.updates });
      });
    }
    if (plan.adds.length) {
      chain = chain.then(function () {
        return cartMutationFetch('/cart/add.js', { items: plan.adds });
      });
    }
    chain
      .then(function (finalCart) {
        applyCartRefreshSections(finalCart && finalCart.sections);
        vbShowNotice(text);
      })
      .catch(function () {
        // Reconcile failed — render the original (pre-reconcile) cart so the drawer
        // matches the un-gifted state rather than going stale.
        applyCartRefreshSections(cart && cart.sections);
      })
      .then(function () {
        vbGiftReconciling = false;
        if (vbReconcilePending) { vbReconcilePending = false; reconcileBundleGifts(); }
      });
    return true;
  }

  // Reconcile after every cart mutation (qty step, remove, add). The guard plus
  // the plan's idempotence (empty once gifts match the tier) prevent a loop.
  document.addEventListener('theme:cart:updated', function () {
    reconcileBundleGifts();
  });
  // Catch a stale cart already loaded on page open (gifts left over from a prior
  // session below the current tier).
  reconcileBundleGifts();

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    var isQuickAdd = form.classList.contains('product-card__quick-add-form');
    var isArticleShopStory = form.classList.contains('article-page__story-form');
    if (!isQuickAdd && !isArticleShopStory) return;

    e.preventDefault();

    var btn = form.querySelector('button[type="submit"][name="add"]') || form.querySelector('button[type="submit"]');
    if (btn && (btn.disabled || btn.classList.contains('is-loading'))) return;

    var idEl = form.querySelector('input[name="id"], select[name="id"]');
    var variantId = idEl ? parseInt(idEl.value, 10) : NaN;
    if (isNaN(variantId)) return;

    var qtyInput = form.querySelector('input[name="quantity"]');
    var qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    if (isNaN(qty) || qty < 1) qty = 1;

    if (!window.SerenCart || typeof window.SerenCart.addToCart !== 'function') return;

    if (btn) {
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
    }

    window.SerenCart.addToCart(variantId, qty)
      .then(function () {
        if (typeof window.openCartDrawer === 'function') {
          window.openCartDrawer(btn);
        }
      })
      .catch(function () {
        if (window.SerenToast) window.SerenToast.cartAddError();
      })
      .finally(function () {
        if (btn) {
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-busy');
        }
      });
  });
})();

/* -------------------------------------------------------------------------- */
/* FAQ + accordion: aria-expanded sync on native <details>                    */
/* -------------------------------------------------------------------------- */

(function () {
  function syncDetailsSummary(details) {
    var summary =
      details.querySelector('.faq__q') || details.querySelector('.accordion__summary');
    if (summary) {
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    }
  }

  document.addEventListener(
    'toggle',
    function (e) {
      var t = e.target;
      if (t.tagName !== 'DETAILS') return;
      if (!t.classList.contains('faq__item') && !t.classList.contains('accordion')) return;
      syncDetailsSummary(t);
    },
    true
  );

  function initDetailsAria() {
    document.querySelectorAll('details.faq__item, details.accordion').forEach(syncDetailsSummary);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDetailsAria);
  } else {
    initDetailsAria();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    if (ev.target) {
      ev.target.querySelectorAll('details.faq__item, details.accordion').forEach(syncDetailsSummary);
    }
  });
})();

/* -------------------------------------------------------------------------- */
/* Scroll reveal (Phase 0 Step 0.10) — do not add .reveal-on-scroll above fold */
/* -------------------------------------------------------------------------- */

(function () {
  var revealObserver = null;

  /* Arm: sections.css carries a `reveal-self-heal` keyframe (2.5s delay,
     forwards) so slow-JS visitors never sit on blank sections. Adding
     `js-reveal-armed` to <html> cancels that fallback because JS is alive
     and the IntersectionObserver will own revealing. Only arm while the
     self-heal hasn't fired yet — if this script executes AFTER the CSS
     already revealed everything, re-hiding it would flash; in that case we
     skip arming and initRevealOnScroll marks nodes `.is-revealed` outright
     (content stays visible, the scroll animation is skipped for this
     pageview). 2400ms (vs the CSS 2.5s delay) leaves margin for the gap
     between navigation start and first style application. */
  var armed = false;
  var elapsedMs =
    typeof performance !== 'undefined' && performance.now ? performance.now() : 0;
  if (elapsedMs < 2400) {
    document.documentElement.classList.add('js-reveal-armed');
    armed = true;
  }

  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    if (!('IntersectionObserver' in window)) return null;
    revealObserver = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -2%', threshold: 0.01 }
    );
    return revealObserver;
  }

  function initRevealOnScroll() {
    var nodes = document.querySelectorAll('.reveal-on-scroll:not(.is-revealed)');
    if (nodes.length === 0) return;

    var obs = getRevealObserver();
    if (!obs || !armed) {
      nodes.forEach(function (el) {
        el.classList.add('is-revealed');
      });
      return;
    }

    nodes.forEach(function (el) {
      obs.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRevealOnScroll);
  } else {
    initRevealOnScroll();
  }

  document.addEventListener('shopify:section:load', initRevealOnScroll);
})();

/* -------------------------------------------------------------------------- */
/* Preloader — min 280ms (so fast cached loads don't flash in and back out),  */
/* max 900ms (cap on slow connections), or window.load — whichever's last/    */
/* first within those bounds. sessionStorage flag so it only runs on the      */
/* first page of the browser session.                                         */
/*                                                                            */
/* NOTE: this IIFE is the POLISHED dismissal only. Because theme.js is        */
/* deferred, its timers start at script execution, not at first paint — so   */
/* the true wall-clock cap (the painted page is never hidden longer than     */
/* ~1.2s) lives in two parse-time fallbacks: the inline <script> next to the */
/* overlay in layout/theme.liquid (sets the session flag too) and the        */
/* `preloader-auto-out` keyframe in base.css (fully JS-independent). All     */
/* three paths are idempotent — whichever fires first wins, the others       */
/* no-op.                                                                    */
/* -------------------------------------------------------------------------- */

(function () {
  var el = document.getElementById('Preloader');
  if (!el) return;
  if (el.style.display === 'none') return;

  // Reduced motion: bypass the overlay entirely. Session flag still set so
  // follow-up pages in the same session behave consistently.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    el.style.display = 'none';
    try {
      sessionStorage.setItem('seren_preloaded', '1');
    } catch (e) {
      /* ignore */
    }
    return;
  }

  /** Minimum time the mark is visible — avoids pop-in/pop-out flash when the
   *  page loads very fast (cached), while still feeling snappy. */
  var MIN_VISIBLE_MS = 280;
  /** Hard cap if window.load never fires in a reasonable window (slow network,
   *  blocked resource). Keeps the overlay from overstaying. */
  var MAX_VISIBLE_MS = 900;
  /** Matches the longest exit transition in base.css (.preloader backdrop). */
  var EXIT_TRANSITION_MS = 420;

  var now = function () {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  };
  var start = now();
  var done = false;

  function finalize() {
    el.classList.add('preloader--out');
    /* aria-hidden="true" is already on the element from initial markup;
       no need to re-set it here. */
    try {
      sessionStorage.setItem('seren_preloaded', '1');
    } catch (e) {
      /* ignore */
    }
    window.setTimeout(function () {
      el.style.display = 'none';
    }, EXIT_TRANSITION_MS);
  }

  function dismiss() {
    if (done) return;
    done = true;
    var elapsed = now() - start;
    var wait = Math.max(0, MIN_VISIBLE_MS - elapsed);
    if (wait > 0) {
      window.setTimeout(finalize, wait);
    } else {
      finalize();
    }
  }

  var maxTimer = window.setTimeout(dismiss, MAX_VISIBLE_MS);
  /* Listen to DOMContentLoaded (vs window.load) so the dismiss fires
     once the DOM is parsed/interactive, not after every below-the-fold
     image and font has resolved. The MIN_VISIBLE_MS guard inside
     dismiss() still prevents the flash-and-back-out on instant cache
     hits. theme.js is loaded with `defer` so by the time this IIFE
     executes the parser may be already past 'loading' — handle both
     cases. */
  function onContentReady() {
    window.clearTimeout(maxTimer);
    dismiss();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onContentReady);
  } else {
    onContentReady();
  }
})();

/* -------------------------------------------------------------------------- */
/* Urgency / momentum block — daily-cycling progress bar + ships-by date      */
/* -------------------------------------------------------------------------- */
/* Server renders an initial fill from now's minute-of-day fraction + a
   ships-by date from today + lead_days (so first paint is correct for
   crawlers + JS-disabled visitors). This IIFE recomputes both on
   DOMContentLoaded against the BROWSER's local clock — fixes stale dates
   under full-page caching where the server-rendered date may be hours or
   days out of sync — and ticks the bar every 60 s for live progression.
   The interval is torn down via shopify:section:unload + an idle-check
   inside tickAll so we're not running forever on a page without a bar. */

(function () {
  var tickIntervalId = null;

  function computePercent(start, end) {
    var d = new Date();
    var minutes = d.getHours() * 60 + d.getMinutes();
    var frac = Math.max(0, Math.min(1, minutes / 1440));
    return Math.round(start + (end - start) * frac);
  }

  function updateBar(el) {
    var start = parseFloat(el.getAttribute('data-progress-start'));
    var end = parseFloat(el.getAttribute('data-progress-end'));
    if (isNaN(start) || isNaN(end)) return;
    if (end < start) end = start;
    var pct = computePercent(start, end);
    var fill = el.querySelector('[data-product-urgency-fill]');
    if (fill) fill.style.width = pct + '%';
    /* Mirror the fill width onto the bar element as a custom property —
       not strictly used today (the fill copy of the label is clipped
       by the fill rectangle's overflow:hidden) but kept in sync for any
       future CSS that wants to reference the live percentage. */
    el.style.setProperty('--urgency-fill', pct + '%');
    /* Update BOTH label copies (track + fill) with identical text. */
    var labels = el.querySelectorAll('[data-product-urgency-label]');
    if (labels.length) {
      var template = el.getAttribute('data-progress-label-template') || '{{ percent }}%';
      var text = template.split('{{ percent }}').join(String(pct));
      for (var i = 0; i < labels.length; i++) labels[i].textContent = text;
    }
  }

  /* Today + n business days (Saturday and Sunday skipped, holidays not),
     the same walk the Liquid does server-side in
     snippets/product-delivery-estimate.liquid + product-urgency.liquid. */
  function addBusinessDays(from, n) {
    var d = new Date(from.getTime());
    var remaining = n;
    var guard = 0;
    while (remaining > 0 && guard < 120) {
      d.setDate(d.getDate() + 1);
      var dow = d.getDay();
      if (dow !== 0 && dow !== 6) remaining -= 1;
      guard += 1;
    }
    return d;
  }

  function formatShipDate(d, locale, withWeekday) {
    /* Use the shop's locale (passed via data-*-locale, defaults to 'en')
       instead of the browser's locale — a Chinese-locale browser on an
       English store would otherwise render "5月8日周五" instead of
       "Wed, May 6". Throws on an unrecognized locale; callers skip. */
    var opts = { month: 'short', day: 'numeric' };
    if (withWeekday) opts.weekday = 'short';
    return new Intl.DateTimeFormat(locale || 'en', opts).format(d);
  }

  function updateShipDates(scope) {
    var root = scope || document;
    var headings = root.querySelectorAll('[data-ship-lead-days], [data-ship-business-days]');
    if (!headings.length) return;
    for (var i = 0; i < headings.length; i++) {
      var d = new Date();
      if (headings[i].hasAttribute('data-ship-business-days')) {
        /* "Ships by" read from the store-wide handling maximum — business
           days, so it lands on the same date as the delivery estimate. */
        var bizDays = parseInt(headings[i].getAttribute('data-ship-business-days'), 10);
        if (isNaN(bizDays) || bizDays <= 0) continue;
        d = addBusinessDays(d, bizDays);
      } else {
        var leadDays = parseInt(headings[i].getAttribute('data-ship-lead-days'), 10);
        if (isNaN(leadDays) || leadDays <= 0) continue;
        d.setDate(d.getDate() + leadDays);
      }
      var formatted;
      try {
        formatted = formatShipDate(d, headings[i].getAttribute('data-ship-locale'), true);
      } catch (e) { continue; }
      var target = headings[i].querySelector('[data-product-urgency-date]');
      if (target) target.textContent = formatted;
    }
  }

  /* Delivery estimate ("Get it Nov 9 – Nov 12", above Add to cart):
     recompute the two dates from the business-day counts the server
     rendered, so a cached page never shows yesterday's dates. */
  function updateDeliveryEstimates(scope) {
    var root = scope || document;
    var lines = root.querySelectorAll('[data-delivery-estimate][data-delivery-max-days]');
    if (!lines.length) return;
    for (var i = 0; i < lines.length; i++) {
      var minDays = parseInt(lines[i].getAttribute('data-delivery-min-days'), 10);
      var maxDays = parseInt(lines[i].getAttribute('data-delivery-max-days'), 10);
      if (isNaN(maxDays) || maxDays <= 0) continue;
      if (isNaN(minDays) || minDays < 0) minDays = maxDays;
      var locale = lines[i].getAttribute('data-delivery-locale');
      var today = new Date();
      var fromEl = lines[i].querySelector('[data-delivery-from]');
      var toEl = lines[i].querySelector('[data-delivery-to]');
      try {
        if (fromEl) fromEl.textContent = formatShipDate(addBusinessDays(today, minDays), locale, false);
        if (toEl) toEl.textContent = formatShipDate(addBusinessDays(today, maxDays), locale, false);
      } catch (e) { continue; }
    }
  }

  function tickAll() {
    var bars = document.querySelectorAll('[data-product-urgency]');
    if (!bars.length) return false;
    for (var i = 0; i < bars.length; i++) updateBar(bars[i]);
    return true;
  }

  function startInterval() {
    if (tickIntervalId !== null) return;
    /* Tick every 60s — fine-grained enough that 1% movement is visible
       within a few minutes on a typical start→end span (28% over 24h ≈
       1% per ~50 min; the timer fires several times per visible step
       so the bar feels live). Reduced-motion guard lives in the CSS
       — the width transition is suppressed there, the value still
       advances. */
    tickIntervalId = setInterval(function () {
      if (!tickAll()) {
        /* No bars left in the DOM (last urgency section was unloaded);
           shut down so we're not running forever on a page without
           one. */
        clearInterval(tickIntervalId);
        tickIntervalId = null;
      }
    }, 60000);
  }

  function stopIntervalIfIdle() {
    if (tickIntervalId === null) return;
    if (document.querySelectorAll('[data-product-urgency]').length === 0) {
      clearInterval(tickIntervalId);
      tickIntervalId = null;
    }
  }

  function init() {
    updateShipDates();
    updateDeliveryEstimates();
    if (!tickAll()) return;
    startInterval();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  /* Theme editor — re-init on section reload, tear down when unloaded. */
  document.addEventListener('shopify:section:load', function (e) {
    updateShipDates(e.target);
    updateDeliveryEstimates(e.target);
    if (tickAll()) startInterval();
  });
  document.addEventListener('shopify:section:unload', stopIntervalIfIdle);
})();

/* -------------------------------------------------------------------------- */
/* Product forms (product, product-spotlight): variants, sticky ATC           */
/* -------------------------------------------------------------------------- */

(function () {
  'use strict';

  /* #theme-strings is parsed once, by window.t (Toast block above). */
  function getMoneyFormat() {
    return window.t('money_format', '${{amount}}');
  }

  /* Prefer Intl.NumberFormat with the visitor's ACTIVE currency (rendered
     server-side from cart.currency.iso_code into theme-strings). Markets-
     converted prices flow into this function as customer-currency cents
     via [data-cents]; without Intl we'd format them with shop.money_format
     which has the SHOP'S primary currency code/symbol baked in — a CAD
     visitor on a USD-base store would see "$55.21 USD" with the right
     amount but a misleading label. Intl picks the symbol + decimal count
     for the active currency automatically (yen has 0 decimals, BTC has 8,
     etc.) so we don't have to maintain those quirks ourselves.

     Falls back to the legacy shop.money_format substitution path when
     Intl is unavailable (very old browsers) or when the active currency
     code is missing — both edge cases. */
  var moneyFormatterCache = null;
  var moneyFormatterCacheKey = null;
  function getIntlMoneyFormatter(iso) {
    if (!iso) iso = window.t('active_currency');
    if (!iso) return null;
    if (typeof Intl === 'undefined' || !Intl.NumberFormat) return null;
    if (moneyFormatterCache && moneyFormatterCacheKey === iso) return moneyFormatterCache;
    try {
      moneyFormatterCache = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: iso,
        currencyDisplay: 'narrowSymbol'
      });
      moneyFormatterCacheKey = iso;
      return moneyFormatterCache;
    } catch (e) {
      return null;
    }
  }

  /* Resolve the buyer's DISPLAY currency + the rate to apply to shop-currency
     cents — handles both Shopify multi-currency modes AND base currency:
       • "Use local currencies" (AUTOMATIC): window.Shopify.currency.active is
         the buyer's currency and .rate converts shop cents → display. Our cents
         (raw Liquid) are shop-currency, so multiply by rate.
       • Fixed prices per market (PRESENTMENT): cart.currency.iso_code
         (active_currency) is the buyer's currency and Liquid already emitted
         presentment cents → rate 1.
       • Base currency: display == shop everywhere → null (no conversion). */
  function currencyContext() {
    var shop = window.t('shop_currency');
    var sc = window.Shopify && window.Shopify.currency;
    if (sc && sc.active && shop && sc.active !== shop) {
      var rate = Number(sc.rate);
      return { active: sc.active, rate: isFinite(rate) && rate > 0 ? rate : 1 };
    }
    var ac = window.t('active_currency');
    if (ac && shop && ac !== shop) return { active: ac, rate: 1 };
    return null;
  }

  function formatMoneyCents(cents) {
    cents = Math.round(Number(cents) || 0);
    var ctx = currencyContext();
    var displayCents = ctx ? Math.round(cents * ctx.rate) : cents;
    var fmt = getIntlMoneyFormatter(ctx ? ctx.active : window.t('active_currency'));
    if (fmt) return fmt.format(displayCents / 100);

    /* Legacy fallback — shop.money_format substitution. Only reached when Intl
       is unavailable or the currency code didn't resolve. Uses displayCents so
       the amount stays rate-adjusted on the fallback path. */
    var format = getMoneyFormat();
    var amount = (displayCents / 100).toFixed(2);
    var noDec = String(Math.round(displayCents / 100));
    return format
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(
        /\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
        noDec.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      );
  }

  function updateProductStickyTotal(root) {
    if (!root) return;
    var sticky = root.querySelector('[data-sticky-atc-price]');
    if (!sticky) return;
    var form = root.querySelector('[data-product-form]');
    var qtyEl = form && form.querySelector('input[name="quantity"]');
    var qty = qtyEl ? parseInt(qtyEl.value, 10) : 1;
    if (isNaN(qty) || qty < 1) qty = 1;
    var cents = readSubscriptionCents(root) || readActiveVariantCentsFromMain(root);
    if (isNaN(cents.unit)) return;

    /* Savings chip sync — call from both code paths below (theme tier and
       bundler-app override) with whatever wasPrice / discountedLine apply to
       that path, so the chip always reflects what the bar's price + strike
       are showing. Reads `data-savings-format` set by Liquid from the
       merchant's sticky_savings_display setting; templates come from
       theme-strings so translations land through the same i18n flow as the
       rest of the JS-rendered copy. */
    function syncSavingsChip(wasPrice, discountedLine) {
      var chip = root.querySelector('[data-sticky-atc-savings]');
      if (!chip) return;
      var format = chip.getAttribute('data-savings-format');
      if (format && format !== 'none' && wasPrice > discountedLine) {
        var savedCents = wasPrice - discountedLine;
        if (format === 'percent') {
          var pct = Math.round(savedCents * 100 / wasPrice);
          var pctTpl = window.t('sticky_savings_percent', '{{ percent }}% off');
          chip.textContent = pctTpl.split('{{ percent }}').join(String(pct));
        } else {
          var savedTpl = window.t('sticky_savings_dollar', '{{ amount }} saved');
          chip.textContent = savedTpl.split('{{ amount }}').join(formatMoneyCents(savedCents));
        }
        chip.hidden = false;
      } else if (chip.hasAttribute('data-savings-format')) {
        chip.textContent = '';
        chip.hidden = true;
      }
    }

    /* Bundle / upsell apps may override the sticky total via the portable
       `bundle:total-changed` CustomEvent (handler below). When set, render
       it verbatim — the app is the source of truth for its own discount
       math (multi-tier rules, A/B-tested codes, auto-applied promos that
       the theme can't model). The strikethrough and savings chip are
       still ours to compute (variant compare-at × qty, fallback to qty ×
       unit) — the chip needs strike-vs-app-total so it doesn't hold a
       stale variant-only % from the initial Liquid render. */
    var appTotal = parseInt(root.getAttribute('data-bundle-app-total'), 10);
    if (!isNaN(appTotal) && appTotal > 0) {
      sticky.textContent = formatMoneyCents(appTotal);
      var unitApp = !isNaN(cents.unit) ? cents.unit : 0;
      var compareApp = !isNaN(cents.compare) && cents.compare > 0 ? cents.compare : unitApp;
      var origLineApp = compareApp * qty;
      var stickyCompareApp = root.querySelector('[data-sticky-atc-compare]');
      if (stickyCompareApp) {
        stickyCompareApp.textContent = origLineApp > appTotal ? formatMoneyCents(origLineApp) : '';
      }
      syncSavingsChip(origLineApp, appTotal);
      return;
    }

    var originalLine = cents.unit * qty;

    sticky.textContent = formatMoneyCents(originalLine);

    var stickyCompare = root.querySelector('[data-sticky-atc-compare]');
    var compareLine = cents.compare * qty;
    var wasPrice = compareLine > originalLine ? compareLine : originalLine;
    if (stickyCompare) {
      if (wasPrice > originalLine) {
        stickyCompare.textContent = formatMoneyCents(wasPrice);
      } else {
        stickyCompare.textContent = '';
      }
    }

    syncSavingsChip(wasPrice, originalLine);
  }

  window.SerenTheme = window.SerenTheme || {};
  window.SerenTheme.formatMoneyCents = formatMoneyCents;
  window.SerenTheme.updateProductStickyTotal = updateProductStickyTotal;

  /* ── Display-currency money reformat ──────────────────────────────────────
     Shopify renders SSR `{{ cents | money }}` in the SHOP'S currency/symbol; for
     a foreign buyer the amount + symbol must be corrected client-side. Under
     "use local currencies" (automatic) the SHOP cents need multiplying by
     Shopify.currency.rate; under fixed-price markets the cents are already
     presentment (rate 1). Either way, any element that opts in with
     `data-money="<shop-cents>"` is re-stamped via formatMoneyCents (which reads
     currencyContext → applies the rate + the buyer's currency format).

     SAFETY (load-bearing): formatMoneyCents/currencyContext are a hard no-op
     when the display currency == the shop currency → base-currency shoppers get
     byte-identical output. NaN/empty/negative cents are skipped (a malformed
     hook leaves the server value untouched, never worse). Idempotent. Only
     touches `[data-money]` (a single full money value); savings sentences +
     the sticky bar manage their own text and carry no data-money. */
  function reformatDisplayMoney(scope) {
    if (!currencyContext()) return;
    var root = scope && scope.querySelectorAll ? scope : document;
    var els = root.querySelectorAll('[data-money]');
    for (var i = 0; i < els.length; i++) {
      var cents = parseInt(els[i].getAttribute('data-money'), 10);
      if (!isFinite(cents) || cents < 0) continue;
      els[i].textContent = formatMoneyCents(cents);
    }
    // Money interpolated mid-sentence via Shopify's `t` filter can't carry a
    // child span — `t` HTML-escapes injected markup. Instead the element
    // declares the shop-formatted string (data-money-shop) + its cents
    // (data-money-replace); we string-replace the shop money with the converted
    // one in place. No-op for base currency (the whole fn returns above).
    var repl = root.querySelectorAll('[data-money-replace]');
    for (var r = 0; r < repl.length; r++) {
      var rc = parseInt(repl[r].getAttribute('data-money-replace'), 10);
      var shopStr = repl[r].getAttribute('data-money-shop');
      if (!shopStr || !isFinite(rc) || rc < 0) continue;
      var conv = formatMoneyCents(rc);
      if (conv && repl[r].textContent.indexOf(shopStr) !== -1) {
        repl[r].textContent = repl[r].textContent.split(shopStr).join(conv);
      }
    }
  }
  window.SerenTheme.reformatDisplayMoney = reformatDisplayMoney;

  /* ── Free-shipping bar: convert the shop-currency threshold to the buyer's
     currency ────────────────────────────────────────────────────────────────
     The free-ship threshold is a merchant setting in the shop's BASE currency
     (e.g. $50 → 5000 cents). Under Markets presentment, cart.total_price arrives
     in the BUYER'S currency (e.g. SEK cents), so a raw `total >= 5000` compares
     SEK against USD and false-unlocks free shipping. We lift the threshold into
     the cart's currency via the live base→active rate (window.Shopify.currency
     .rate) and recompute unlock / remaining / percent.

     Works in any currency: base-currency carts (cart currency == shop currency)
     get rate 1 → identical to the server render. Foreign carts convert via the
     rate. If a foreign cart exposes no trustworthy rate, the bar hides rather
     than risk a false unlock. The SSR (data-ship-* attrs) carries the
     shop-currency threshold + the raw cart total + both currency codes. */

  /* Learn the store's money format (symbol placement + decimal/group separators)
     from a price already rendered on the page, so our converted amount matches
     it EXACTLY — including currencies that put the symbol after ("228,67 kr")
     and locale separators that Intl with the default locale wouldn't reproduce. */
  function detectPageMoneyFormat() {
    var els = document.querySelectorAll('[data-money]');
    for (var i = 0; i < els.length; i++) {
      var c = parseInt(els[i].getAttribute('data-money'), 10);
      var t = (els[i].textContent || '').replace(/ /g, ' ').replace(/\s+/g, ' ').trim();
      if (!isFinite(c) || c <= 0 || !/\d/.test(t)) continue;
      var m = t.match(/\d[\d., ]*\d|\d/);
      if (!m) continue;
      var numStr = m[0];
      var at = t.indexOf(numStr);
      var dec = '.';
      var dm = numStr.match(/([.,])(\d{1,2})$/);
      if (dm) dec = dm[1];
      return {
        prefix: t.slice(0, at),
        suffix: t.slice(at + numStr.length),
        dec: dec,
        group: dec === '.' ? ',' : '.',
      };
    }
    return null;
  }
  function formatWithDetected(fmt, cents) {
    var neg = cents < 0;
    cents = Math.abs(Math.round(cents));
    var intPart = Math.floor(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, fmt.group);
    var frac = (cents % 100).toString();
    if (frac.length < 2) frac = '0' + frac;
    return (neg ? '-' : '') + fmt.prefix + intPart + fmt.dec + frac + fmt.suffix;
  }

  function applyShipBar(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var bars = root.querySelectorAll('[data-ship-bar]');
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      /* Subscriptions ship free (r11): the server marked this cart as shipping
         free because it holds a selling-plan line (global setting on). Keep
         the met state whatever the rate says — there is nothing to convert. */
      if (bar.hasAttribute('data-ship-subscription-free')) {
        bar.hidden = false;
        var subMsg = bar.querySelector('[data-ship-msg]');
        var subMetClass = bar.getAttribute('data-ship-met-class');
        if (subMsg) {
          var subMetText = bar.getAttribute('data-ship-met-text');
          if (subMetText) subMsg.textContent = subMetText;
          if (subMetClass) subMsg.classList.add(subMetClass);
        }
        var subFill = bar.querySelector('[data-ship-fill]');
        if (subFill) subFill.style.setProperty('--ship-pct', '100%');
        var subTrack = bar.querySelector('[role="progressbar"]');
        if (subTrack) subTrack.setAttribute('aria-valuenow', '100');
        continue;
      }
      var base = parseInt(bar.getAttribute('data-ship-threshold-base'), 10); // shop-currency cents
      var total = parseInt(bar.getAttribute('data-ship-total'), 10); // cart-currency cents
      if (!isFinite(base) || base <= 0 || !isFinite(total) || total < 0) continue;

      var cartCur = bar.getAttribute('data-ship-cart-currency');
      var sc = window.Shopify && window.Shopify.currency;
      var activeCur = sc && sc.active;

      // Sanity: if the cart's currency differs from the currency the rate is
      // quoted in, the rate wouldn't convert correctly — hide rather than risk
      // a wrong unlock. (Normally cart currency === active currency.)
      if (cartCur && activeCur && cartCur !== activeCur) {
        bar.hidden = true;
        continue;
      }
      bar.hidden = false;

      // Shopify.currency.rate is the shop-base -> active-currency rate (1 for a
      // base-currency buyer, e.g. 9.53 for a SEK buyer on a USD store). The
      // threshold is in the shop base and the cart total in the active currency,
      // so threshold * rate lands both in the same currency to compare.
      var rate = 1;
      var r = sc ? parseFloat(sc.rate) : NaN;
      if (isFinite(r) && r > 0) rate = r;

      var threshold = Math.round(base * rate); // cart-currency cents
      if (threshold <= 0) continue;
      var unlocked = total >= threshold;
      var remaining = Math.max(0, threshold - total);
      var pct = Math.min(100, Math.max(0, Math.round((total * 100) / threshold)));

      var msg = bar.querySelector('[data-ship-msg]');
      var metClass = bar.getAttribute('data-ship-met-class');
      if (msg) {
        if (unlocked) {
          var metText = bar.getAttribute('data-ship-met-text');
          if (metText) msg.textContent = metText;
          if (metClass) msg.classList.add(metClass);
        } else {
          // `remaining` is already in the buyer's (active) currency. Match the
          // store's own money format (read from a price already on the page) so
          // symbol placement + separators are exact — Shopify renders SEK as
          // "228,67 kr" (symbol after) which Intl's default locale wouldn't.
          // Fall back to the active currency via Intl, then formatMoneyCents.
          var detected = detectPageMoneyFormat();
          var amountStr;
          if (detected) {
            amountStr = formatWithDetected(detected, remaining);
          } else {
            var fmtCur = activeCur || cartCur;
            var ifmt = fmtCur ? getIntlMoneyFormatter(fmtCur) : null;
            amountStr = ifmt ? ifmt.format(remaining / 100) : formatMoneyCents(remaining);
          }
          var tpl = bar.getAttribute('data-ship-away-tpl') || '';
          msg.textContent = tpl.split('__SHIP_AMOUNT__').join(amountStr);
          if (metClass) msg.classList.remove(metClass);
        }
      }
      var fill = bar.querySelector('[data-ship-fill]');
      if (fill) fill.style.setProperty('--ship-pct', pct + '%');
      var track = bar.querySelector('[role="progressbar"]');
      if (track) track.setAttribute('aria-valuenow', String(pct));
    }
  }
  window.SerenTheme.applyShipBar = applyShipBar;

  function refreshCurrencyUI(scope) {
    reformatDisplayMoney(scope || document);
    applyShipBar(scope || document);
  }

  /* Registered UNCONDITIONALLY — the gate is runtime (Shopify.currency loads as
     a global before DOMContentLoaded under automatic conversion), and the
     functions themselves no-op for base currency. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      refreshCurrencyUI(document);
    });
  } else {
    refreshCurrencyUI(document);
  }
  /* Cart drawer / sections re-render SSR money on every update — re-stamp the
     fresh DOM. Scoped to the event target when present, document otherwise. */
  document.addEventListener('theme:cart:updated', function () {
    refreshCurrencyUI(document);
  });
  document.addEventListener('cart:refresh', function () {
    refreshCurrencyUI(document);
  });
  document.addEventListener('shopify:section:load', function (e) {
    refreshCurrencyUI(e && e.target ? e.target : document);
  });
  /* The cart drawer replaces its whole .shopify-section (so closure refs rebind)
     and fires this instead of shopify:section:load. refreshCartDiscounts() does
     this on drawer OPEN without a following theme:cart:updated, so without this
     listener the fresh SSR (e.g. the un-converted "0.00 kr away") would show. */
  document.addEventListener('theme:cart-drawer:replaced', function () {
    refreshCurrencyUI(document);
  });

  function parseJSONScript(id) {
    var el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function findExactVariant(variants, selected) {
    return variants.find(function (v) {
      if (!v.options || v.options.length !== selected.length) return false;
      return v.options.every(function (o, i) {
        return o === selected[i];
      });
    });
  }

  function firstAvailableForValue(variants, optIdx, value, selected) {
    var candidates = variants.filter(function (v) {
      if (v.options[optIdx] !== value) return false;
      for (var i = 0; i < selected.length; i++) {
        if (i === optIdx) continue;
        if (selected[i] && v.options[i] !== selected[i]) return false;
      }
      return true;
    });
    var avail = candidates.find(function (v) {
      return v.available;
    });
    return avail || candidates[0] || null;
  }

  function isOptionValueAvailable(variants, selections, optIdx, value) {
    return variants.some(function (v) {
      if (v.options[optIdx] !== value) return false;
      for (var i = 0; i < selections.length; i++) {
        if (i === optIdx) continue;
        if (selections[i] && v.options[i] !== selections[i]) return false;
      }
      return v.available;
    });
  }

  function updatePickerAvailability(root, variants, selections) {
    root.querySelectorAll('[data-variant-picker-choice]').forEach(function (btn) {
      var idx = parseInt(btn.getAttribute('data-option-index'), 10);
      var val = btn.getAttribute('data-value');
      if (isNaN(idx) || val == null) return;
      var ok = isOptionValueAvailable(variants, selections, idx, val);
      btn.classList.toggle('is-unavailable', !ok);
      btn.setAttribute('data-available', ok ? 'true' : 'false');
    });
    root.querySelectorAll('[data-variant-picker-select]').forEach(function (sel) {
      var idx = parseInt(sel.getAttribute('data-option-index'), 10);
      if (isNaN(idx)) return;
      Array.prototype.forEach.call(sel.options, function (opt) {
        if (opt.disabled && opt.value === '') return;
        var ok = isOptionValueAvailable(variants, selections, idx, opt.value);
        opt.disabled = !ok && opt.value !== selections[idx];
        if (opt.value === selections[idx]) opt.disabled = false;
      });
    });
  }

  function syncPickerUI(picker, options) {
    picker.querySelectorAll('[data-variant-picker-choice]').forEach(function (btn) {
      var idx = parseInt(btn.getAttribute('data-option-index'), 10);
      var val = btn.getAttribute('data-value');
      var on = !isNaN(idx) && options[idx] === val;
      btn.classList.toggle('is-selected', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    picker.querySelectorAll('[data-variant-picker-select]').forEach(function (sel) {
      var idx = parseInt(sel.getAttribute('data-option-index'), 10);
      if (!isNaN(idx) && options[idx] != null) sel.value = options[idx];
    });
  }

  function setVariant(root, variant, variants, pushUrl, picker, selections) {
    if (!variant) return;
    var idInput = root.querySelector('[data-product-variant-id]');
    if (idInput) idInput.value = variant.id;

    root.querySelectorAll('[data-variant-id]').forEach(function (el) {
      el.classList.toggle('is-active', String(el.getAttribute('data-variant-id')) === String(variant.id));
    });

    var stickyPrice = root.querySelector('[data-sticky-atc-price]');
    var activePrice = root.querySelector('.product__variant-price.is-active .price__current');
    if (stickyPrice && activePrice) {
      stickyPrice.textContent = activePrice.textContent;
    }

    root.querySelectorAll('.product__variant-savings').forEach(function (el) {
      el.classList.toggle('is-active', String(el.getAttribute('data-variant-id')) === String(variant.id));
    });

    /* ATC + express-checkout state on variant change. Three things flip
       together when shoppers pick a sold-out variant:
         1) disable the submit button + .is-disabled class for styling,
         2) swap the button label between Add-to-cart and Sold-out
            (reading data-add-label / data-sold-label off the wrapper —
            server-rendered so locale-correct without a JS string table),
         3) hide the dynamic-checkout wrapper so Apple/Shop Pay can't
            bypass the disabled ATC.
       Without (2), the disabled button keeps the previous variant's
       "Add to cart" label and the OOS state reads as ambiguous. */
    var submitWrap = root.querySelector('[data-product-atc]');
    var submitBtn = root.querySelector('.product__add-button');
    if (submitBtn && submitBtn.tagName === 'BUTTON') {
      submitBtn.disabled = !variant.available;
      submitBtn.classList.toggle('is-disabled', !variant.available);
      if (submitWrap) {
        var addLabel = submitWrap.getAttribute('data-add-label');
        var soldLabel = submitWrap.getAttribute('data-sold-label');
        var nextLabel = variant.available ? addLabel : soldLabel;
        if (nextLabel && submitBtn.textContent !== nextLabel) {
          submitBtn.textContent = nextLabel;
        }
      }
    }
    /* Sticky bar button mirrors the main ATC's sold-out state + label. */
    var stickyAddBtn = root.querySelector('.sticky-atc__add');
    if (stickyAddBtn && stickyAddBtn.tagName === 'BUTTON' && submitWrap) {
      stickyAddBtn.disabled = !variant.available;
      stickyAddBtn.classList.toggle('is-disabled', !variant.available);
      if (variant.available) {
        stickyAddBtn.removeAttribute('aria-disabled');
      } else {
        stickyAddBtn.setAttribute('aria-disabled', 'true');
      }
      var stickyLabel = submitWrap.getAttribute(variant.available ? 'data-add-label' : 'data-sold-label');
      if (stickyLabel && stickyAddBtn.textContent.trim() !== stickyLabel) {
        stickyAddBtn.textContent = stickyLabel;
      }
    }

    var dynamicCheckout = root.querySelector('[data-product-dynamic-checkout]');
    if (dynamicCheckout) {
      if (variant.available) {
        dynamicCheckout.removeAttribute('hidden');
      } else {
        dynamicCheckout.setAttribute('hidden', '');
      }
    }

    if (picker && selections && variant.options) {
      selections.length = 0;
      variant.options.forEach(function (o) {
        selections.push(o);
      });
      syncPickerUI(picker, selections);
      updatePickerAvailability(picker, variants, selections);
    }

    if (
      pushUrl &&
      !root.hasAttribute('data-no-variant-url') &&
      window.history &&
      window.history.replaceState
    ) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url.toString());
    }

    /* Clear any bundle-app override BEFORE dispatching variant-change. The
       sequence matters: clear → dispatch → app's listener re-fires
       bundle:total-changed for the new variant → theme's handler sets the
       fresh override → updateProductStickyTotal below renders the latest.
       Clearing AFTER the dispatch (or in a separate document listener) would
       silently overwrite an app's same-tick re-fire. */
    root.removeAttribute('data-bundle-app-total');

    document.dispatchEvent(
      new CustomEvent('theme:product:variant-change', {
        /* root is additive: lets per-root listeners (sticky bar, terms line)
           ignore changes fired by another product root on the same page. */
        detail: { variant: variant, root: root },
      })
    );

    updateProductStickyTotal(root);
  }

  function readActiveVariantCentsFromMain(main) {
    if (!main) return { unit: NaN, compare: 0 };
    var cur = main.querySelector('.product__variant-price.is-active .price__current');
    var cmpEl = main.querySelector('.product__variant-price.is-active .price__compare');
    var unit = cur ? parseInt(cur.getAttribute('data-cents'), 10) : NaN;
    var cmp = cmpEl ? parseInt(cmpEl.getAttribute('data-compare-cents'), 10) : 0;
    if (isNaN(cmp)) cmp = 0;
    return { unit: unit, compare: cmp };
  }

  /* The subscription app's plan field in a product form (Kaching writes
     selling_plan; other apps use the id[] / _id names). */
  function sellingPlanField(form) {
    if (!form) return null;
    return (
      form.querySelector('[name="selling_plan"]') ||
      form.querySelector('[name="id[selling_plan]"]') ||
      form.querySelector('[name="selling_plan_id"]')
    );
  }

  /* Selling plan id the buy box shows (NaN / <= 0 = one-time). The app's field
     is the source of truth once it exists. Until then, a plan the server
     rendered as pre-selected (form[data-sub-preselect], set by
     sections/product.liquid when the subscription starts selected) holds, so
     the first paint never drops back to one-time while the app loads. Once
     the field has been seen, no field means one-time (Kaching removes it for
     one-time). If it never appears (app off), the hold ends a few seconds
     after load (watchSubPreselect) and the page settles on one-time. */
  var subPreselectExpired = false;
  function readSellingPlanId(form) {
    if (!form) return NaN;
    var sp = sellingPlanField(form);
    if (sp) {
      form.__serenSubFieldSeen = true;
      return parseInt(sp.value, 10);
    }
    if (subPreselectExpired || form.__serenSubFieldSeen) return NaN;
    return parseInt(form.getAttribute('data-sub-preselect'), 10);
  }

  /* Ends the pre-selected hold when no app field shows up: 4 s after load,
     then tells the subscription watchers to re-read (one-time). */
  var subPreselectWatched = false;
  function watchSubPreselect(root) {
    var form = root && root.querySelector('[data-product-form]');
    if (subPreselectWatched || !form) return;
    var pre = parseInt(form.getAttribute('data-sub-preselect'), 10);
    if (isNaN(pre) || pre <= 0) return;
    subPreselectWatched = true;
    var expire = function () {
      window.setTimeout(function () {
        subPreselectExpired = true;
        document.dispatchEvent(new CustomEvent('theme:product:purchase-mode-settled'));
      }, 4000);
    };
    if (document.readyState === 'complete') expire();
    else window.addEventListener('load', expire, { once: true });
  }

  /* Sticky-bar subscription price. Subscribe is "active" when the Kaching
     selling_plan field holds a plan id — works for BOTH tier-card products and
     plain (single-variant) products. The sub unit comes from the selected tier
     card's data-sub-cents, or — when there are no tier cards — the active
     hidden price block's data-sub-cents (variant.selling_plan_allocations).
     Returns the regular compare-at (MSRP) as the "was", falling back to the
     one-time price; null when not subscribed so the sticky bar uses the
     one-time reader (one-time mode + non-subscription products unchanged). */
  function readSubscriptionCents(root) {
    if (!root) return null;
    var planId = readSellingPlanId(root.querySelector('[data-product-form]'));
    if (isNaN(planId) || planId <= 0) return null;

    /* Tier-card products: the selected card carries sub + compare cents. */
    var container = root.querySelector('.variant-picker__choices--tier');
    if (container) {
      var sel = container.querySelector('[data-variant-picker-choice].is-selected');
      var cardPricing = sel && sel.querySelector('.variant-picker__card-pricing');
      if (cardPricing) {
        var cardSub = parseInt(cardPricing.getAttribute('data-sub-cents'), 10);
        if (!isNaN(cardSub) && cardSub > 0) {
          var cardOne = parseInt(cardPricing.getAttribute('data-onetime-cents'), 10);
          var cardCmp = parseInt(cardPricing.getAttribute('data-compare-cents'), 10);
          if (isNaN(cardCmp)) cardCmp = 0;
          return { unit: cardSub, compare: cardCmp > 0 ? cardCmp : (isNaN(cardOne) ? 0 : cardOne) };
        }
      }
    }

    /* Non-tier (single-variant) products: the active variant's price block
       carries data-sub-cents (the per-delivery subscription price). */
    var active = root.querySelector('.product__variant-price.is-active');
    if (active) {
      var subH = parseInt(active.getAttribute('data-sub-cents'), 10);
      if (!isNaN(subH) && subH > 0) {
        var curEl = active.querySelector('.price__current');
        var cmpEl = active.querySelector('.price__compare');
        var oneH = curEl ? parseInt(curEl.getAttribute('data-cents'), 10) : NaN;
        var cmpH = cmpEl ? parseInt(cmpEl.getAttribute('data-compare-cents'), 10) : 0;
        if (isNaN(cmpH)) cmpH = 0;
        return { unit: subH, compare: cmpH > 0 ? cmpH : (isNaN(oneH) ? 0 : oneH) };
      }
    }
    return null;
  }

  /* ----------------------------------------------------------------------
     Tier-card subscribe / one-time dynamic re-pricing.

     Wholly additive + inert by default. Activates only when ALL of:
       1) a `.variant-picker__choices--tier` container exists in the product
          root (single-option price-varying products that opt into rich tier
          cards — see snippets/variant-picker.liquid), AND
       2) at least one card carries a positive `data-sub-cents` (the product
          has a subscription selling plan), AND
       3) a subscription app (Kaching, etc.) injects a `selling_plan` field
          into the product form so the shopper can toggle Subscribe / one-time.

     With no tier container -> returns immediately (the vast majority of PDPs).
     With a tier container but no `data-sub-cents` -> the cards are already
     correct as server-rendered (no subscription); we wire NOTHING. With both
     but no Kaching field ever appearing -> the watcher self-stops and the
     cards stay in their server-rendered one-time state. NEVER throws if the
     sub app is absent or its markup differs.

     The cards are server-rendered with `| money` (SEO/GEO-safe); this only
     re-renders the visible price/strike/savings text in place when the
     shopper flips the Kaching toggle — it never injects price that wasn't
     already in the HTML for one-time mode.
     -------------------------------------------------------------------- */
  function initTierPurchaseMode(root) {
    var container = root.querySelector('.variant-picker__choices--tier');
    if (!container) return;

    var cards = Array.prototype.slice.call(
      container.querySelectorAll('[data-variant-picker-choice]')
    );
    if (!cards.length) return;

    /* No card advertises a subscription price -> nothing to re-price; the
       server-rendered (one-time) cards are already correct + static. */
    var hasSub = cards.some(function (card) {
      var pricing = card.querySelector('.variant-picker__card-pricing');
      if (!pricing) return false;
      var sub = parseInt(pricing.getAttribute('data-sub-cents'), 10);
      return !isNaN(sub) && sub > 0;
    });
    if (!hasSub) return;

    var savingsFormat = container.getAttribute('data-savings-format') || 'none';

    /* i18n-resilient savings templates. The cards ship server-rendered with
       the merchant's locale savings copy already in [data-card-savings];
       reverse-engineer the template from the first card so JS re-renders use
       the SAME locale string (e.g. "Save $5.00" / "{{ percent }}% off" or any
       translation) instead of a hardcoded English literal. Falls back to the
       known default templates if capture fails (e.g. a card whose initial
       savings text was empty). */
    var amountTemplate = 'Save {{ amount }}';
    var percentTemplate = '{{ percent }}% off';
    (function captureTemplates() {
      for (var i = 0; i < cards.length; i++) {
        var pricing = cards[i].querySelector('.variant-picker__card-pricing');
        if (!pricing) continue;
        var savingsEl = pricing.querySelector('[data-card-savings]');
        if (!savingsEl) continue;
        var text = (savingsEl.textContent || '').trim();
        if (!text) continue;
        if (savingsFormat === 'percent') {
          var pct = parseInt(savingsEl.getAttribute('data-save-pct'), 10);
          if (!isNaN(pct) && text.indexOf(String(pct)) !== -1) {
            percentTemplate = text.split(String(pct)).join('{{ percent }}');
            return;
          }
        } else if (savingsFormat === 'amount') {
          var amtCents = parseInt(savingsEl.getAttribute('data-save-amt-cents'), 10);
          if (!isNaN(amtCents) && amtCents > 0) {
            var money = formatMoneyCents(amtCents);
            if (text.indexOf(money) !== -1) {
              amountTemplate = text.split(money).join('{{ amount }}');
              return;
            }
          }
        }
      }
    })();

    /* Per-unit "$X each" template — same reverse-engineer trick as the savings
       template below, so the re-rendered unit price keeps the merchant's locale
       string. The unit element lives OUTSIDE the pricing block (in the card
       body); we recompute it on every mode flip from the active price ÷ units,
       mirroring the server's `price | divided_by: tier_units | money`. Falls
       back to "{{ price }} each" if capture fails. */
    var perUnitTemplate = '{{ price }} each';
    /* Cards are server-rendered in subscription mode when the subscription
       starts selected (data-purchase-mode="subscription"); read the per-unit
       text against the price the card actually shows. */
    var serverSubMode = container.getAttribute('data-purchase-mode') === 'subscription';
    (function captureUnitTemplate() {
      for (var i = 0; i < cards.length; i++) {
        var unitEl = cards[i].querySelector('[data-card-unit]');
        var pricing = cards[i].querySelector('.variant-picker__card-pricing');
        if (!unitEl || !pricing) continue;
        var units = parseInt(unitEl.getAttribute('data-units'), 10);
        var onetime = parseInt(pricing.getAttribute('data-onetime-cents'), 10);
        var shownSub = parseInt(pricing.getAttribute('data-sub-cents'), 10);
        if (serverSubMode && !isNaN(shownSub) && shownSub > 0) onetime = shownSub;
        if (isNaN(units) || units <= 0 || isNaN(onetime)) continue;
        var money = formatMoneyCents(Math.floor(onetime / units));
        var text = (unitEl.textContent || '').trim();
        if (text && text.indexOf(money) !== -1) {
          perUnitTemplate = text.split(money).join('{{ price }}');
          return;
        }
      }
    })();

    function renderSavings(savingsEl, savedCents, compareCents) {
      if (savingsFormat === 'none' || savedCents <= 0 || compareCents <= 0) {
        savingsEl.textContent = '';
        savingsEl.hidden = true;
        savingsEl.setAttribute('data-save-amt-cents', '0');
        savingsEl.setAttribute('data-save-pct', '0');
        return;
      }
      savingsEl.setAttribute('data-save-amt-cents', String(savedCents));
      var pct = Math.round((savedCents * 100) / compareCents);
      savingsEl.setAttribute('data-save-pct', String(pct));
      if (savingsFormat === 'percent') {
        savingsEl.textContent = percentTemplate.split('{{ percent }}').join(String(pct));
      } else {
        savingsEl.textContent = amountTemplate.split('{{ amount }}').join(formatMoneyCents(savedCents));
      }
      savingsEl.hidden = false;
    }

    /* Recompute every card's price / compare / savings for the active mode.
       Batched inside one rAF so all reads happen before the writes — no
       layout thrash even with many cards. SUB mode: price = subscription
       per-delivery price, "was" = the regular compare-at / MSRP (struck; falls
       back to the one-time price when no compare-at is set), savings =
       compare-at - sub. ONE-TIME mode: price = one-time price, "was" =
       compare-at (struck), savings = compare-at - onetime. */
    /* One polite status node (after the group) announces the SELECTED card
       when a shopper's subscribe / one-time flip re-prices the cards —
       instead of an aria-live group re-reading every card. Only within 2s
       of a real interaction with the form, so the subscription app's own
       pre-selection on page load stays silent. */
    var statusEl = container.parentNode
      ? container.parentNode.querySelector('[data-tier-status]')
      : null;
    var lastUserInputAt = 0;
    function announceSelected() {
      if (!statusEl || Date.now() - lastUserInputAt > 2000) return;
      var sel = container.querySelector('[data-variant-picker-choice].is-selected');
      if (!sel) return;
      var parts = [];
      ['.variant-picker__card-label', '[data-card-price]', '[data-card-unit]'].forEach(
        function (selector) {
          var el = sel.querySelector(selector);
          var text = el && !el.hidden ? (el.textContent || '').trim() : '';
          if (text) parts.push(text);
        }
      );
      statusEl.textContent = parts.join(', ');
    }

    function applyMode(mode) {
      if (container.getAttribute('data-purchase-mode') === mode) return;
      container.setAttribute('data-purchase-mode', mode);
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(function () {
          repriceAll(mode);
          updateProductStickyTotal(root);
          announceSelected();
        });
      } else {
        repriceAll(mode);
        updateProductStickyTotal(root);
        announceSelected();
      }
    }

    function repriceAll(mode) {
      for (var i = 0; i < cards.length; i++) {
        var pricing = cards[i].querySelector('.variant-picker__card-pricing');
        if (!pricing) continue;
        var onetime = parseInt(pricing.getAttribute('data-onetime-cents'), 10);
        if (isNaN(onetime)) continue;
        var compareAt = parseInt(pricing.getAttribute('data-compare-cents'), 10);
        if (isNaN(compareAt)) compareAt = 0;
        var sub = parseInt(pricing.getAttribute('data-sub-cents'), 10);

        var priceCents;
        var compareCents;
        if (mode === 'subscription' && !isNaN(sub) && sub > 0) {
          priceCents = sub;
          /* Strike the REGULAR compare-at (MSRP), not the one-time price, so the
             "was" + savings read against the original price. Fall back to the
             one-time price only when the variant has no compare-at set. */
          compareCents = compareAt > 0 ? compareAt : onetime;
        } else {
          priceCents = onetime;
          compareCents = compareAt;
        }

        var priceEl = pricing.querySelector('[data-card-price]');
        if (priceEl) {
          priceEl.textContent = formatMoneyCents(priceCents);
          priceEl.setAttribute('data-money', String(priceCents));
        }

        var compareEl = pricing.querySelector('[data-card-compare]');
        var comparePrefixEl = pricing.querySelector('[data-card-compare-prefix]');
        var savedCents = compareCents > priceCents ? compareCents - priceCents : 0;
        if (compareEl) {
          if (savedCents > 0) {
            compareEl.textContent = formatMoneyCents(compareCents);
            compareEl.setAttribute('data-money', String(compareCents));
            compareEl.hidden = false;
          } else {
            compareEl.textContent = '';
            compareEl.hidden = true;
          }
          /* The hidden "Was" goes with the strike it introduces. */
          if (comparePrefixEl) comparePrefixEl.hidden = compareEl.hidden;
        }

        var savingsEl = pricing.querySelector('[data-card-savings]');
        if (savingsEl) renderSavings(savingsEl, savedCents, compareCents);

        /* Per-unit "$X each" — recompute from the ACTIVE price so it tracks the
           subscribe / one-time toggle (the element sits in the card body, not
           the pricing block, so query the card). Mirrors the server's
           integer-division per-unit cents. */
        var unitEl = cards[i].querySelector('[data-card-unit]');
        if (unitEl) {
          var units = parseInt(unitEl.getAttribute('data-units'), 10);
          if (!isNaN(units) && units > 0) {
            unitEl.textContent = perUnitTemplate
              .split('{{ price }}')
              .join(formatMoneyCents(Math.floor(priceCents / units)));
          }
        }

        /* Free-shipping badge — show when the ACTIVE-mode price clears the
           threshold, so it tracks the subscribe / one-time flip (a sub price
           can drop a tier below the threshold). Inert when no badge or no
           threshold is present. */
        var freeshipEl = cards[i].querySelector('[data-card-freeship]');
        if (freeshipEl) {
          var fsThreshold = parseInt(
            container.getAttribute('data-freeship-threshold-cents'),
            10
          );
          /* Subscriptions ship free (r11): with the global setting on
             (data-freeship-subscriptions on the container) every card ships
             free while Subscribe is selected; one-time keeps the threshold
             rule, and with no threshold the cue hides again on the flip. */
          var fsSubscriptions = container.hasAttribute('data-freeship-subscriptions');
          if (fsSubscriptions && mode === 'subscription') {
            freeshipEl.hidden = false;
          } else if (!isNaN(fsThreshold) && fsThreshold > 0) {
            freeshipEl.hidden = priceCents < fsThreshold;
          } else if (fsSubscriptions) {
            freeshipEl.hidden = true;
          }
        }
      }
    }

    /* Read the Kaching (or any sub app) selling-plan state off the product
       form. A positive integer in the selling_plan field => subscription
       selected; empty / absent / 0 => one-time. Mirrors the names the ATC
       harvest already reads (selling_plan / id[selling_plan] / selling_plan_id). */
    var form = root.querySelector('[data-product-form]');
    function readMode() {
      var v = readSellingPlanId(form);
      return !isNaN(v) && v > 0 ? 'subscription' : 'onetime';
    }

    var destroyed = false;
    var debounceId = null;
    function sync() {
      if (destroyed) return;
      if (debounceId !== null) return;
      debounceId = window.setTimeout(function () {
        debounceId = null;
        applyMode(readMode());
      }, 80);
    }

    /* Delegated form listeners — fire when the shopper clicks a Kaching
       Subscribe / one-time radio (the app mutates the selling_plan field). */
    var onFormInput = function () {
      sync();
    };
    /* On the product root, not the form: the subscription widget's own UI
       may sit outside the <form> it writes selling_plan into. */
    var onUserInput = function (e) {
      if (e.isTrusted) lastUserInputAt = Date.now();
    };
    root.addEventListener('pointerdown', onUserInput, true);
    root.addEventListener('keydown', onUserInput, true);
    if (form) {
      form.addEventListener('input', onFormInput);
      form.addEventListener('change', onFormInput);
      /* Capture-phase change as the primary watcher — catches the selling_plan
         field's change even if a sub app stops propagation in the bubble phase. */
      form.addEventListener('change', onFormInput, true);
    }

    /* MutationObserver — Kaching may rewrite the selling_plan value via JS
       (setting .value without firing input/change) or inject the field after
       our init. Watch the form subtree for childList + attribute (value)
       mutations and re-sync. */
    var mo = null;
    if (form && typeof window.MutationObserver === 'function') {
      mo = new MutationObserver(function () {
        sync();
      });
      mo.observe(form, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value'],
      });
    }

    /* Polling fallback — covers apps that set .value programmatically with no
       observable DOM mutation (some frameworks set the property, not the
       attribute). Self-stops once the selling_plan field is present AND its
       value has been stable for 3 ticks, or after a 15s hard cap — so we are
       never polling indefinitely on a page the shopper has settled on. */
    var pollId = null;
    if (form) {
      var lastSeen = null;
      var stableTicks = 0;
      var elapsed = 0;
      pollId = window.setInterval(function () {
        if (destroyed) {
          clearPoll();
          return;
        }
        elapsed += 250;
        if (!document.contains(form)) {
          clearPoll();
          return;
        }
        var sp =
          form.querySelector('[name="selling_plan"]') ||
          form.querySelector('[name="id[selling_plan]"]') ||
          form.querySelector('[name="selling_plan_id"]');
        var current = sp ? sp.value : null;
        if (current === lastSeen) {
          if (sp) stableTicks++;
        } else {
          lastSeen = current;
          stableTicks = 0;
          sync();
        }
        if ((sp && stableTicks >= 3) || elapsed >= 15000) clearPoll();
      }, 250);
    }
    function clearPoll() {
      if (pollId !== null) {
        clearInterval(pollId);
        pollId = null;
      }
    }

    /* A tier switch re-runs setVariant which dispatches variant-change; keep
       the active purchase mode applied to the (newly selected) card set.
       Re-render unconditionally so the just-activated card reflects the mode
       immediately — applyMode short-circuits when the mode is unchanged, so
       call repriceAll directly here. */
    var onVariantChange = function () {
      if (destroyed) return;
      repriceAll(container.getAttribute('data-purchase-mode') || 'onetime');
    };
    document.addEventListener('theme:product:variant-change', onVariantChange);
    /* The pre-selected hold ended without an app field: settle on one-time. */
    document.addEventListener('theme:product:purchase-mode-settled', sync);

    /* Teardown — disconnect observer, clear timers + listeners on navigation
       away so nothing leaks (pagehide fires for bfcache + real unload). */
    var onPageHide = function () {
      destroyed = true;
      if (debounceId !== null) {
        clearTimeout(debounceId);
        debounceId = null;
      }
      clearPoll();
      if (mo) {
        mo.disconnect();
        mo = null;
      }
      if (form) {
        form.removeEventListener('input', onFormInput);
        form.removeEventListener('change', onFormInput);
        form.removeEventListener('change', onFormInput, true);
      }
      root.removeEventListener('pointerdown', onUserInput, true);
      root.removeEventListener('keydown', onUserInput, true);
      document.removeEventListener('theme:product:variant-change', onVariantChange);
      document.removeEventListener('theme:product:purchase-mode-settled', sync);
      window.removeEventListener('pagehide', onPageHide);
    };
    window.addEventListener('pagehide', onPageHide);

    /* Initial paint — apply whatever mode the form is in right now (Kaching
       defaults to one-time, but a returning shopper / pre-selected plan could
       land on subscription). */
    applyMode(readMode());
  }

  /* Sticky-bar subscribe sync for products WITHOUT tier cards (single-variant
     / non-multipack Kaching products). Tier products are handled by
     initTierPurchaseMode (its applyMode re-syncs the sticky). Gate: skip when a
     tier container exists (avoid double-watching) or no variant advertises a
     subscription price. Watches the Kaching selling_plan field + recomputes the
     sticky total on toggle so the bar follows the subscribe price. */
  function initStickySubscriptionSync(root) {
    if (!root || root.querySelector('.variant-picker__choices--tier')) return;
    var form = root.querySelector('[data-product-form]');
    if (!form) return;
    var hasSub = Array.prototype.some.call(
      root.querySelectorAll('.product__variant-price[data-sub-cents]'),
      function (el) {
        var s = parseInt(el.getAttribute('data-sub-cents'), 10);
        return !isNaN(s) && s > 0;
      }
    );
    if (!hasSub) return;

    var destroyed = false;
    var resync = function () {
      if (!destroyed) updateProductStickyTotal(root);
    };
    /* Capture-phase change catches the Kaching toggle even if it stops
       bubbling; input covers immediate field writes. */
    form.addEventListener('change', resync, true);
    form.addEventListener('input', resync);
    document.addEventListener('theme:product:purchase-mode-settled', resync);

    /* Kaching injects + sets the selling_plan field client-side; poll briefly
       so the initial (possibly subscribe-preselected) state lands, then
       self-stop. Resyncs only when the field value actually changes. */
    var lastVal = null;
    var ticks = 0;
    var poll = window.setInterval(function () {
      if (destroyed || !document.contains(form)) {
        window.clearInterval(poll);
        return;
      }
      var sp =
        form.querySelector('[name="selling_plan"]') ||
        form.querySelector('[name="id[selling_plan]"]') ||
        form.querySelector('[name="selling_plan_id"]');
      var v = sp ? sp.value : null;
      if (v !== lastVal) {
        lastVal = v;
        resync();
      }
      if (++ticks >= 40) window.clearInterval(poll);
    }, 250);

    var onHide = function () {
      destroyed = true;
      window.clearInterval(poll);
      form.removeEventListener('change', resync, true);
      form.removeEventListener('input', resync);
      document.removeEventListener('theme:product:purchase-mode-settled', resync);
      window.removeEventListener('pagehide', onHide);
    };
    window.addEventListener('pagehide', onHide);
  }

  /* Subscription terms line (sections/product.liquid, [data-sub-terms]).
     Visible only while the form's selling_plan field holds a plan — the
     subscribe option is app-injected (Kaching), so nothing shows without it.
     Price = the selected plan's per-delivery price for the active variant
     (× quantity), interval = the plan's server-parsed interval (blank →
     the "on your schedule" template). "Cancel anytime" shows unless the
     plan's server-side "cancel" flag is false (its name already says it).
     Same watcher shape as
     initTierPurchaseMode: delegated change/input/click + a MutationObserver
     + a short poll for apps that set .value silently. A polite status node
     announces the terms when subscribe is chosen or the price changes. */
  function initSubscriptionTerms(root) {
    var wrap = root && root.querySelector('[data-sub-terms]');
    var form = root && root.querySelector('[data-product-form]');
    if (!wrap || !form) return;
    var lead = wrap.querySelector('[data-sub-terms-lead]');
    var renews = wrap.querySelector('[data-sub-terms-renews]');
    var cancelLink = wrap.querySelector('[data-sub-terms-cancel]');
    var status = root.querySelector('[data-sub-terms-status]');
    var data = {};
    try {
      data = JSON.parse(wrap.querySelector('[data-sub-terms-data]').textContent) || {};
    } catch (e) {
      data = {};
    }
    var plans = data.plans || {};
    var cancels = data.cancel || {};
    var prices = data.prices || {};
    var tplEvery = lead ? lead.getAttribute('data-tpl-every') || '' : '';
    var tplSchedule = lead ? lead.getAttribute('data-tpl-schedule') || '' : '';
    var lastAnnounced = '';

    function planId() {
      var v = readSellingPlanId(form);
      return !isNaN(v) && v > 0 ? String(v) : null;
    }

    function render() {
      var pid = planId();
      if (!pid) {
        wrap.hidden = true;
        if (status && lastAnnounced !== '') status.textContent = '';
        lastAnnounced = '';
        return;
      }
      var idInput = form.querySelector('[data-product-variant-id]');
      var vid = idInput ? String(idInput.value) : '';
      var cents = prices[vid] && typeof prices[vid][pid] === 'number' ? prices[vid][pid] : NaN;
      if (isNaN(cents) || cents <= 0) {
        /* Plan not in the server map (e.g. an app-only plan id): fall back to
           the active variant's first allocation, same source as the sticky. */
        var active = root.querySelector('.product__variant-price.is-active');
        cents = active ? parseInt(active.getAttribute('data-sub-cents'), 10) : NaN;
      }
      var qtyEl = form.querySelector('input[name="quantity"]');
      var qty = qtyEl ? parseInt(qtyEl.value, 10) : 1;
      if (isNaN(qty) || qty < 1) qty = 1;
      if (lead) {
        if (!isNaN(cents) && cents > 0) {
          var interval = plans[pid] || '';
          var tpl = interval ? tplEvery.split('__INTERVAL__').join(interval) : tplSchedule;
          /* Write only on change — the form MutationObserver above would
             otherwise re-schedule on our own text write, forever. */
          var nextLead = tpl.split('__PRICE__').join(formatMoneyCents(cents * qty));
          if (lead.textContent !== nextLead) lead.textContent = nextLead;
          lead.hidden = false;
        } else {
          if (lead.textContent !== '') lead.textContent = '';
          lead.hidden = true;
        }
      }
      if (cancelLink) {
        /* Unknown plan ids (not in the server map) keep the link. */
        var hideCancel = cancels[pid] === false;
        if (cancelLink.hidden !== hideCancel) cancelLink.hidden = hideCancel;
      }
      wrap.hidden = false;
      /* "Auto-renews $18.00 every month" — the visible parts minus the
         CSS-drawn "·" separators. */
      var announce = ((renews ? renews.textContent + ' ' : '') + (lead && !lead.hidden ? lead.textContent : '')).trim();
      if (status && announce !== lastAnnounced) {
        status.textContent = announce;
        lastAnnounced = announce;
      }
    }

    var destroyed = false;
    var t1 = null;
    var t2 = null;
    function schedule() {
      if (destroyed) return;
      /* Read twice: once after the app's own handler has run, once more for
         apps that write the field asynchronously. */
      if (t1 !== null) clearTimeout(t1);
      if (t2 !== null) clearTimeout(t2);
      t1 = window.setTimeout(function () { t1 = null; render(); }, 60);
      t2 = window.setTimeout(function () { t2 = null; render(); }, 360);
    }

    form.addEventListener('change', schedule, true);
    form.addEventListener('input', schedule);
    form.addEventListener('click', schedule, true);
    var mo = null;
    if (typeof window.MutationObserver === 'function') {
      mo = new MutationObserver(schedule);
      mo.observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'checked'] });
    }
    var onVariant = function (e) {
      if (e && e.detail && e.detail.root && e.detail.root !== root) return;
      schedule();
    };
    document.addEventListener('theme:product:variant-change', onVariant);
    document.addEventListener('theme:product:purchase-mode-settled', schedule);

    var lastVal = null;
    var ticks = 0;
    var poll = window.setInterval(function () {
      if (destroyed || !document.contains(form) || ++ticks > 60) {
        window.clearInterval(poll);
        return;
      }
      var v = planId();
      if (v !== lastVal) {
        lastVal = v;
        render();
      }
    }, 250);

    var onHide = function () {
      destroyed = true;
      window.clearInterval(poll);
      if (t1 !== null) clearTimeout(t1);
      if (t2 !== null) clearTimeout(t2);
      if (mo) mo.disconnect();
      form.removeEventListener('change', schedule, true);
      form.removeEventListener('input', schedule);
      form.removeEventListener('click', schedule, true);
      document.removeEventListener('theme:product:variant-change', onVariant);
      document.removeEventListener('theme:product:purchase-mode-settled', schedule);
      window.removeEventListener('pagehide', onHide);
    };
    window.addEventListener('pagehide', onHide);

    render();
  }

  function initProduct(root) {
    if (!root || root.getAttribute('data-product-init') === 'true') return;
    root.setAttribute('data-product-init', 'true');

    var sectionId = root.getAttribute('data-section-id');
    if (!sectionId) return;

    var data = parseJSONScript('ProductData-' + sectionId);
    var variants = data && data.variants ? data.variants : [];
    var selections = data && data.selectedOptions ? data.selectedOptions.slice() : [];

    var picker = root.querySelector('[data-variant-picker]');
    var variantParam = null;
    try {
      variantParam = new URLSearchParams(window.location.search).get('variant');
    } catch (e) {
      variantParam = null;
    }
    var urlVariant =
      variantParam && variants.length
        ? variants.find(function (v) {
            return String(v.id) === String(variantParam);
          })
        : null;

    if (picker && variants.length > 1) {
      if (urlVariant) {
        setVariant(root, urlVariant, variants, false, picker, selections);
      } else {
        updatePickerAvailability(picker, variants, selections);
        syncPickerUI(picker, selections);
      }

      picker.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-variant-picker-choice]');
        if (!btn || btn.closest('[data-variant-picker]') !== picker) return;
        var idx = parseInt(btn.getAttribute('data-option-index'), 10);
        var val = btn.getAttribute('data-value');
        if (isNaN(idx) || val == null) return;

        selections[idx] = val;
        var variant = findExactVariant(variants, selections);
        if (!variant) {
          variant = firstAvailableForValue(variants, idx, val, selections);
          if (variant) {
            selections = variant.options.slice();
            syncPickerUI(picker, selections);
          }
        }
        if (variant) {
          setVariant(root, variant, variants, true, picker, selections);
        }
      });

      picker.addEventListener('change', function (e) {
        var sel = e.target.closest('[data-variant-picker-select]');
        if (!sel || sel.closest('[data-variant-picker]') !== picker) return;
        var idx = parseInt(sel.getAttribute('data-option-index'), 10);
        if (isNaN(idx)) return;
        selections[idx] = sel.value;
        var variant = findExactVariant(variants, selections);
        if (!variant) {
          variant = firstAvailableForValue(variants, idx, sel.value, selections);
          if (variant) {
            selections = variant.options.slice();
            syncPickerUI(picker, selections);
          }
        }
        if (variant) {
          setVariant(root, variant, variants, true, picker, selections);
        }
      });
    } else if (urlVariant) {
      setVariant(root, urlVariant, variants, false, null, null);
    }

    /* Tier-card subscribe/one-time re-pricing — self-gating + inert unless
       this product renders rich tier cards WITH a subscription selling plan
       AND a sub app injects a selling_plan field. No-op on every other PDP. */
    initTierPurchaseMode(root);
    initStickySubscriptionSync(root);
    initSubscriptionTerms(root);
    watchSubPreselect(root);

    var atcTarget = root.querySelector('[data-product-atc]');
    var stickyBar = root.querySelector('[data-sticky-atc]');
    if (atcTarget && stickyBar && 'IntersectionObserver' in window) {
      /* Show only once the main ATC has left via the TOP of the viewport —
         not on first paint when it simply sits below the fold (mobile).
         Hidden = aria-hidden + inert (+ visibility:hidden in CSS), so the
         bar's button is out of the tab order until it is on screen. */
      var obs = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            stickyBar.classList.toggle('is-visible', show);
            stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
            if (show) stickyBar.removeAttribute('inert');
            else stickyBar.setAttribute('inert', '');
          });
        },
        { threshold: 0, rootMargin: '0px 0px 0px 0px' }
      );
      obs.observe(atcTarget);
    }

    var stickyAdd = root.querySelector('.sticky-atc__add');
    /* Match either the PDP main ATC (.product__add-button) or the
       product-spotlight section's submit button. Falling back to the form's
       own submit button covers any future product-rendering section that
       uses initProduct without inventing a new button class. */
    var realAdd =
      root.querySelector('.product__add-button') ||
      root.querySelector('.product-spotlight__submit button[type="submit"]');
    if (stickyAdd && realAdd) {
      stickyAdd.addEventListener('click', function (e) {
        e.preventDefault();
        realAdd.click();
      });
    }

    var form = root.querySelector('[data-product-form]');
    if (form) {
      /* Last-resort fallback: any submit button inside the form. Without this,
         a section that wires up [data-product-form] but uses neither the PDP
         nor spotlight button class would fall through to native form POST and
         redirect to /cart. */
      var addBtn = realAdd || form.querySelector('button[type="submit"][name="add"]') || form.querySelector('button[type="submit"]');
      if (addBtn) {
        form.addEventListener('submit', function (e) {
          /* Always intercept so the browser never redirects to /cart — open the drawer instead. */
          e.preventDefault();
          addBtn.classList.add('is-loading');
          addBtn.setAttribute('aria-busy', 'true');
          var idInput = form.querySelector('[data-product-variant-id]');
          var qtyInput = form.querySelector('input[name="quantity"]');
          var mainId = idInput ? parseInt(idInput.value, 10) : NaN;
          var qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
          if (isNaN(qty) || qty < 1) qty = 1;
          if (isNaN(mainId)) {
            addBtn.classList.remove('is-loading');
            addBtn.removeAttribute('aria-busy');
            return;
          }
          var items = [{ id: mainId, quantity: qty }];
          /* Bundle stackable unit: when this product has a volume-bundle widget,
             mint a unique id and stamp it on the main line + its gift lines below.
             Each PDP add becomes its OWN cart line (Shopify won't merge lines with
             differing properties) and its gifts slave to that line. Non-bundle
             products get no unit and merge normally. */
          var vbUnit = root.querySelector('.seren-vbundle')
            ? 'u' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
            : null;
          if (vbUnit) items[0].properties = { _bundle_unit: vbUnit };
          var checked = form.querySelectorAll(
            '[data-product-addon]:checked:not([disabled])'
          );
          checked.forEach(function (cb) {
            var vid = parseInt(cb.getAttribute('data-addon-variant-id'), 10);
            if (!isNaN(vid)) items.push({ id: vid, quantity: 1 });
          });
          /* Bundle free-gift lines. The Seren Bundles volume widget publishes
             the selected tier's gift line(s) on its root as
             data-seren-vbundle-gift-items (each tagged _free_gift = bundleId, so
             the bundle discount Function zeroes it at checkout). Merge them so
             the gift rides along atomically with the bundle add — picking a
             gifting tier in the widget is the shopper's consent. De-duped by
             variant id to stay safe with multi-instance pickers (PDP + sticky). */
          /* De-dup ACROSS instances of the SAME bundle (the PDP picker + the
             sticky picker publish identical sets) by bundle id — but PRESERVE
             multiplicity WITHIN a set. A qty-2 gift with both slots on the same
             variant emits [V, V]; collapsing by variant id (the old bug) dropped
             one, so only 1 unit was added and the reconciler then back-filled the
             missing unit with the DEFAULT variant → cart showed the wrong variant.
             Aggregate same-variant lines by SUMMING quantity instead. */
          var giftBundlesSeen = {};
          root.querySelectorAll('.seren-vbundle[data-seren-vbundle-gift-items]').forEach(function (bRoot) {
            var raw = bRoot.getAttribute('data-seren-vbundle-gift-items');
            if (!raw) return;
            var giftLines;
            try { giftLines = JSON.parse(raw); } catch (e) { return; }
            if (!Array.isArray(giftLines) || !giftLines.length) return;
            // De-dup key = the bundle id (a bundle's PDP + sticky pickers publish
            // identical sets), falling back to the raw payload when no id is
            // present — so two identical sets still collapse and two different
            // bundles never do.
            var seenKey =
              (giftLines[0] && giftLines[0].properties && giftLines[0].properties._free_gift) || raw;
            if (giftBundlesSeen[seenKey]) return;
            giftBundlesSeen[seenKey] = true;
            var byKey = {};
            giftLines.forEach(function (gl) {
              if (!gl || gl.id == null) return;
              var gprops = (gl.properties && typeof gl.properties === 'object') ? gl.properties : {};
              if (vbUnit) gprops._bundle_unit = vbUnit;
              var k = String(gl.id) + '|' + JSON.stringify(gprops);
              if (!byKey[k]) {
                byKey[k] = { id: gl.id, quantity: 0 };
                if (Object.keys(gprops).length) byKey[k].properties = gprops;
              }
              byKey[k].quantity += gl.quantity || 1;
            });
            Object.keys(byKey).forEach(function (k) { items.push(byKey[k]); });
          });
          /* Subscription passthrough — additive + inert without a sub app.
             A subscription app (Kaching, Recharge, Skio, …) injects a selling
             plan into the product form; a native submit would carry it, but our
             custom add-to-cart hand-builds the line, so we forward it here. Read
             the form's selling_plan field (it reflects the chosen option — empty
             or absent for one-time, or when no sub app is installed) and stamp it
             on the MAIN product line only; gifts + addons stay one-time. No field
             / no value → items unchanged → bundle + theme behave exactly as
             before. Wrapped so it can never block the add. */
          try {
            var spInput =
              form.querySelector('[name="selling_plan"]') ||
              form.querySelector('[name="id[selling_plan]"]') ||
              form.querySelector('[name="selling_plan_id"]');
            var spId = spInput && spInput.value ? parseInt(spInput.value, 10) : NaN;
            if (!isNaN(spId) && spId > 0) items[0].selling_plan = spId;
          } catch (e) {}
          /* Form-field harvest — additive + inert when absent. Same root cause
             as the selling_plan passthrough above: our custom add-to-cart
             hand-builds the line, so anything an app injects into the product
             form (that a native submit would carry) must be read explicitly or
             it's silently dropped. Harvest three families:
             • properties[...] → line-item properties (engraving / gift-message /
               product-options apps, AND Shopify's gift-card recipient fields),
               merged onto the MAIN line. Shopify drops blank non-"_" values
               server-side, exactly as a native submit would.
             • attributes[...] → cart-level attributes (affiliate/UTM, gift wrap,
               delivery date), applied via updateAttributes after the add.
             • extra [name="id"] inputs beyond the variant → additional lines
               (frequently-bought-together / multi-add apps).
             Wrapped so a malformed app field can never block the add. */
          var cartAttributes = null;
          try {
            var harvestedProps = {};
            var hasHarvestedProps = false;
            var harvestedAttrs = {};
            var hasHarvestedAttrs = false;
            new FormData(form).forEach(function (value, key) {
              var pm = key.match(/^properties\[(.+)\]$/);
              if (pm) {
                harvestedProps[pm[1]] = value;
                hasHarvestedProps = true;
                return;
              }
              var am = key.match(/^attributes\[(.+)\]$/);
              if (am) {
                harvestedAttrs[am[1]] = value;
                hasHarvestedAttrs = true;
              }
            });
            if (hasHarvestedProps) {
              items[0].properties = Object.assign({}, items[0].properties || {}, harvestedProps);
            }
            if (hasHarvestedAttrs) cartAttributes = harvestedAttrs;
            /* App-injected extra variant lines: the variant input carries
               [data-product-variant-id]; any OTHER name="id" is an extra line. */
            form.querySelectorAll('[name="id"]:not([data-product-variant-id])').forEach(function (inp) {
              var xid = parseInt(inp.value, 10);
              if (!isNaN(xid) && xid > 0 && xid !== mainId) items.push({ id: xid, quantity: 1 });
            });
          } catch (e) {}
          if (!window.SerenCart || typeof SerenCart.addItems !== 'function') {
            addBtn.classList.remove('is-loading');
            addBtn.removeAttribute('aria-busy');
            return;
          }
          window.SerenCart.addItems(items)
            .then(function () {
              /* Persist any harvested cart attributes (fire-and-forget — they
                 save to the cart for checkout; the drawer doesn't depend on them
                 and updateAttributes skips the section re-render). */
              if (cartAttributes && window.SerenCart && typeof SerenCart.updateAttributes === 'function') {
                SerenCart.updateAttributes(cartAttributes);
              }
              checked.forEach(function (cb) {
                cb.checked = false;
              });
              /* Added from the quick-shop dialog while the drawer is already
                 open (drawer upsell -> picker): openCartDrawer no-ops, so no
                 cart:open fires to close the dialog — close it here. */
              if (form.closest('[data-quick-shop-modal]') && window.SerenQuickShop) {
                window.SerenQuickShop.close();
              }
              if (typeof window.openCartDrawer === 'function') {
                window.openCartDrawer();
              }
              /* M2 — brief check mark on the button once the drawer is open
                 (CSS .is-added). Visual confirmation only; the drawer is the
                 announced surface. */
              addBtn.classList.add('is-added');
              window.setTimeout(function () {
                addBtn.classList.remove('is-added');
              }, 1200);
            })
            .catch(function (err) {
              if (!window.SerenToast) return;
              /* A 422 on an add that carried bundle free-gift lines is almost
                 always the gift variant selling out between render and click
                 (Shopify rejects the whole add). Say so instead of the generic
                 error, so the shopper knows to pick another gift. */
              var hadGift = items.some(function (it) {
                return it && it.properties && it.properties._free_gift;
              });
              var giftMsg = window.t('cart_error_gift_unavailable', '');
              if (hadGift && giftMsg && err && Number(err.status) === 422) {
                window.SerenToast.error(giftMsg, { duration: 6000 });
                return;
              }
              window.SerenToast.cartAddError();
            })
            .finally(function () {
              addBtn.classList.remove('is-loading');
              addBtn.removeAttribute('aria-busy');
            });
        });
      }
      var qtyInput = form.querySelector('input[name="quantity"]');
      if (qtyInput) {
        /* Clear any bundle-app override when qty changes — covers the case
           where the user manually edits the qty input (typing or stepper) while
           a bundle override is active. The bundle's own selectTier dispatches
           qty change/input events too, then immediately re-fires
           bundle:total-changed afterwards, which restores the override with
           the new tier's total. So clearing here is safe for both paths. */
        var onQtyChange = function () {
          root.removeAttribute('data-bundle-app-total');
          updateProductStickyTotal(root);
        };
        qtyInput.addEventListener('input', onQtyChange);
        qtyInput.addEventListener('change', onQtyChange);
      }
    }

    updateProductStickyTotal(root);
  }

  /* Portable bundle-app contract.

     Apps fire `bundle:total-changed` with `{ totalCents, quantity? }` whenever
     their selected tier / discount changes. We stash the override on
     `[data-product]` and re-render the sticky. The event name is intentionally
     theme-agnostic so the same app can ship into any theme that adopts it.

     If `quantity` is provided we sync the form's hidden quantity input so
     ATC submits the right number of units alongside whatever line-item
     payload the app injects.

     setVariant clears the override BEFORE dispatching variant-change, giving
     the app a clean slate to re-fire bundle:total-changed for the new variant
     in its own variant-change listener. If the app doesn't re-fire, the sticky
     falls back to undiscounted unit×qty (correct, just not the app's discount)
     — safer than holding a stale discount applied to the wrong variant's price. */
  document.addEventListener('bundle:total-changed', function (e) {
    if (!e.detail || typeof e.detail.totalCents !== 'number') return;
    var products = document.querySelectorAll('[data-product]');
    for (var i = 0; i < products.length; i++) {
      var root = products[i];
      root.setAttribute('data-bundle-app-total', String(e.detail.totalCents));
      if (typeof e.detail.quantity === 'number' && e.detail.quantity >= 1) {
        var qInput = root.querySelector('[data-product-form] input[name="quantity"]');
        if (qInput && qInput.value !== String(e.detail.quantity)) {
          qInput.value = String(e.detail.quantity);
        }
      }
      updateProductStickyTotal(root);
    }
  });

  function initAllProducts() {
    document.querySelectorAll('[data-product]').forEach(initProduct);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllProducts);
  } else {
    initAllProducts();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    if (!ev.target) return;
    ev.target.querySelectorAll('[data-product]').forEach(initProduct);
  });

  /* Expose initProduct so SerenQuickShop (and any future caller that
     injects [data-product] HTML at runtime) can re-bind variant picker +
     ATC after injection without faking a section:load event. */
  window.SerenTheme = window.SerenTheme || {};
  window.SerenTheme.initProduct = initProduct;
})();

/* -------------------------------------------------------------------------- */
/* Quick Shop modal — open per-card variant + ATC dialog                       */
/* -------------------------------------------------------------------------- */

(function () {
  'use strict';

  /* Multi-variant cards link to the full PDP via [data-quick-shop-trigger];
     this module intercepts the click, fetches the slim quick-shop body via
     the alt product template (`/products/{handle}?view=quick-shop`, which
     sets `{% layout none %}` so the response is just the section markup),
     injects it into the global <dialog> from snippets/quick-shop-modal.liquid,
     and re-runs SerenTheme.initProduct on the injected fragment so the
     variant picker + ATC submit re-bind. If JS is off or fetch fails, the
     link falls back to /products/{handle} — no-JS users + AI crawlers
     still reach the full PDP. */

  var dialog = null;
  var body = null;
  var skeleton = null;
  var errorEl = null;
  var lastTrigger = null;
  var inflightController = null;

  function ensureRefs() {
    if (dialog) return true;
    dialog = document.querySelector('[data-quick-shop-modal]');
    if (!dialog) return false;
    body = dialog.querySelector('[data-quick-shop-body]');
    skeleton = dialog.querySelector('[data-quick-shop-skeleton]');
    errorEl = dialog.querySelector('[data-quick-shop-error]');
    return !!body;
  }

  function showSkeleton() {
    if (!body) return;
    /* Wipe any prior product fragment but keep the skeleton placeholder
       in the DOM so the modal opens with structured first paint. */
    var existing = body.querySelector('[data-quick-shop-body]');
    if (existing) existing.remove();
    var existingFragment = body.querySelector('[data-product]');
    if (existingFragment) existingFragment.remove();
    if (skeleton) skeleton.hidden = false;
    if (errorEl) errorEl.hidden = true;
    body.setAttribute('aria-busy', 'true');
    /* Back to the generic "View options" name until the product loads. */
    if (dialog) dialog.setAttribute('aria-labelledby', 'QuickShopTitle');
  }

  function showError() {
    if (skeleton) skeleton.hidden = true;
    if (errorEl) errorEl.hidden = false;
    if (body) body.setAttribute('aria-busy', 'false');
  }

  function injectFragment(html) {
    if (!body) return;
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');
    /* Shopify wraps section output in `<div class="shopify-section">`
       (with the schema's `class` value appended). Prefer the inner
       `[data-quick-shop-body]` so the dialog's own padding/scroll layout
       owns spacing; fall through to the section wrapper or body for
       defensive completeness. */
    var fragment =
      doc.querySelector('[data-quick-shop-body]') ||
      doc.querySelector('.quick-shop-render') ||
      doc.body;
    if (!fragment) {
      showError();
      return;
    }
    if (skeleton) skeleton.hidden = true;
    if (errorEl) errorEl.hidden = true;
    /* Clear any prior product DOM (skeleton stays as a sibling, hidden). */
    Array.prototype.slice.call(body.children).forEach(function (child) {
      if (child === skeleton) return;
      child.remove();
    });
    body.appendChild(fragment.cloneNode(true));
    body.setAttribute('aria-busy', 'false');

    /* Name the dialog after the product (its injected title heading). */
    var productTitle = body.querySelector('.quick-shop__title');
    if (productTitle && dialog) {
      productTitle.id = 'QuickShopProductTitle';
      dialog.setAttribute('aria-labelledby', 'QuickShopProductTitle');
    }

    var injected = body.querySelector('[data-product]');
    if (injected && window.SerenTheme && typeof SerenTheme.initProduct === 'function') {
      SerenTheme.initProduct(injected);
    }

    /* Move focus to the first interactive task element so keyboard +
       screen-reader users land near the variant picker, not on the close
       button (which is where native <dialog> autofocus would have put
       them). Picker first → ATC fallback → leave focus alone. */
    var firstTask =
      body.querySelector('[data-variant-picker-choice]:not([disabled])') ||
      body.querySelector('[data-variant-picker-select]') ||
      body.querySelector('.product__add-button:not([disabled])');
    if (firstTask && typeof firstTask.focus === 'function') {
      firstTask.focus({ preventScroll: true });
    }
  }

  function openDialog() {
    if (!dialog) return;
    if (typeof dialog.showModal === 'function' && !dialog.open) {
      try {
        dialog.showModal();
      } catch (e) {
        /* Already open or detached — ignore. */
      }
    }
    document.body.classList.add('is-quick-shop-open');
  }

  function closeDialog() {
    if (!dialog) return;
    if (dialog.open && typeof dialog.close === 'function') {
      try {
        dialog.close();
      } catch (e) {
        /* ignore */
      }
    }
    if (inflightController) {
      inflightController.abort();
      inflightController = null;
    }
    document.body.classList.remove('is-quick-shop-open');
    if (lastTrigger && typeof lastTrigger.focus === 'function' && document.contains(lastTrigger)) {
      lastTrigger.focus();
    } else {
      /* Trigger was re-rendered away (a drawer upsell after the add refreshed
         the drawer) — keep focus in the open drawer instead of on <body>. */
      var drawerClose = document.querySelector('[data-cart-drawer].is-open [data-cart-drawer-close]');
      if (drawerClose) drawerClose.focus();
    }
    lastTrigger = null;
  }

  function fetchAndOpen(handle, triggerEl) {
    if (!handle || !ensureRefs()) return;
    lastTrigger = triggerEl || null;
    showSkeleton();
    openDialog();

    if (inflightController) inflightController.abort();
    var ctrl = (window.AbortController) ? new AbortController() : null;
    inflightController = ctrl;

    /* Use the alt product template (`templates/product.quick-shop.liquid`)
       with `{% layout none %}` — Section Rendering API (`?sections=...`)
       is unreliable for sections that aren't part of the current page's
       section group. The alt-template route guarantees `product` context
       is bound and returns the section HTML with no layout chrome. */
    var url = '/products/' + encodeURIComponent(handle) + '?view=quick-shop';
    fetch(url, {
      headers: { Accept: 'text/html' },
      signal: ctrl ? ctrl.signal : undefined,
      credentials: 'same-origin',
    })
      .then(function (r) {
        if (!r.ok) throw new Error('quick-shop ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (ctrl !== inflightController) return;
        if (!html) {
          showError();
          return;
        }
        injectFragment(html);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        showError();
      })
      .finally(function () {
        if (ctrl === inflightController) inflightController = null;
      });
  }

  /* Single delegated click listener handles trigger + close button + backdrop.
     Backdrop clicks land on the <dialog> element itself when they miss the
     `__inner` content (children stop propagation). */
  document.addEventListener('click', function (ev) {
    var target = ev.target;
    if (!target || !target.closest) return;

    var trigger = target.closest('[data-quick-shop-trigger]');
    if (trigger) {
      /* Honor modifier-clicks (open in new tab) — fall through so the
         customer reaches the full PDP. */
      if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey || ev.button === 1) return;
      var handle = trigger.getAttribute('data-product-handle');
      if (!handle) return;
      if (!ensureRefs()) return;
      ev.preventDefault();
      fetchAndOpen(handle, trigger);
      return;
    }

    if (target.closest('[data-quick-shop-close]')) {
      ev.preventDefault();
      closeDialog();
      return;
    }

    if (dialog && target === dialog) {
      closeDialog();
    }
  });

  /* Escape closes (native) — but also clean up the body class + abort
     any in-flight fetch via the close listener. */
  if (typeof document !== 'undefined') {
    document.addEventListener('keydown', function (ev) {
      if (ev.key !== 'Escape') return;
      if (!dialog || !dialog.open) return;
      /* Let the native dialog handle the close; piggyback for cleanup. */
      requestAnimationFrame(function () {
        if (!dialog.open) {
          if (inflightController) {
            inflightController.abort();
            inflightController = null;
          }
          document.body.classList.remove('is-quick-shop-open');
          if (lastTrigger && typeof lastTrigger.focus === 'function') {
            lastTrigger.focus();
            lastTrigger = null;
          }
        }
      });
    });
  }

  /* Close the modal automatically once the cart drawer opens (ATC success
     path: form submit → SerenCart.addItems → openCartDrawer fires). The
     drawer takes focus; the quick-shop dialog behind it is no longer the
     active surface and would otherwise stack visually.

     `cart:open` is dispatched on `window` (see initCartDrawer's openDrawer),
     not `document` — events don't bubble between the two for CustomEvent.
     Listening on `document` here was a no-op and left the dialog open
     after ATC. */
  window.addEventListener('cart:open', function () {
    if (!dialog || !dialog.open) return;
    closeDialog();
  });

  window.SerenQuickShop = {
    open: fetchAndOpen,
    close: closeDialog,
  };
})();

/* -------------------------------------------------------------------------- */
/* Lazy external video (YouTube / Vimeo) — iframe on play click only            */
/* -------------------------------------------------------------------------- */

(function () {
  function mountIframe(wrap, src) {
    if (!wrap || !src) return;
    if (wrap.querySelector('iframe')) return;
    var iframe = document.createElement('iframe');
    iframe.className = 'seren-external-video__iframe';
    iframe.src = src;
    iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('loading', 'lazy');
    var btn = wrap.querySelector('[data-external-video-play]');
    var label = btn && (btn.getAttribute('aria-label') || '').trim();
    iframe.title = label || 'Video';
    wrap.innerHTML = '';
    wrap.appendChild(iframe);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-external-video-play]');
    if (!btn) return;
    var inner = btn.closest('[data-external-video-src]');
    if (!inner) return;
    var src = inner.getAttribute('data-external-video-src');
    if (!src) return;
    e.preventDefault();
    mountIframe(inner, src);
  });
})();
/* ---------------------------------------------------------------------------
 * Inline video player controller (snippets/video-inline.liquid).
 * Lives here so the controller loads once per page, not duplicated on every
 * section render. Features:
 *   - Glass-pill mute (bottom-left) and play/pause (bottom-right) controls.
 *   - Click the video itself to toggle play/pause.
 *   - On play, pause icon flashes briefly then fades away; on pause the
 *     play icon stays visible.
 *   - Autoplay mode falls back to muted if the browser blocks sound.
 *   - Autoplay direct-URL videos pause when scrolled offscreen (saves
 *     bandwidth, CPU, and battery — matches the uploaded-video behaviour
 *     Shopify hints at with `autoplay` on <video>).
 *   - Localized aria-labels come from window.t (#theme-strings, populated
 *     by layout/theme.liquid).
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

  var L_PLAY = window.t('video_play', 'Play video');
  var L_PAUSE = window.t('video_pause', 'Pause video');
  var L_MUTE = window.t('video_mute', 'Mute');
  var L_UNMUTE = window.t('video_unmute', 'Unmute');

  function initVideoInline(wrapper) {
    if (wrapper.getAttribute('data-vi-init') === '1') return;
    wrapper.setAttribute('data-vi-init', '1');

    var player = wrapper.querySelector('.video-inline__player');
    var muteBtn = wrapper.querySelector('.video-inline__mute');
    var ppBtn = wrapper.querySelector('.video-inline__playpause');
    var iconUnmuted = wrapper.querySelector('.video-inline__icon-unmuted');
    var iconMuted = wrapper.querySelector('.video-inline__icon-muted');
    var iconPlay = wrapper.querySelector('.video-inline__icon-play');
    var iconPause = wrapper.querySelector('.video-inline__icon-pause');
    if (!player || !muteBtn || !ppBtn) return;

    var fadeTimer = null;
    var autoplayFlag = wrapper.getAttribute('data-vi-autoplay') === 'true';
    var wasPlayingBeforeOffscreen = false;

    function syncMute() {
      var m = player.muted;
      muteBtn.setAttribute('aria-pressed', m ? 'true' : 'false');
      muteBtn.setAttribute('aria-label', m ? L_UNMUTE : L_MUTE);
      if (iconUnmuted) iconUnmuted.style.display = m ? 'none' : '';
      if (iconMuted) iconMuted.style.display = m ? '' : 'none';
    }

    function showPlayPersistent() {
      if (iconPlay) iconPlay.style.display = '';
      if (iconPause) iconPause.style.display = 'none';
      ppBtn.classList.add('is-visible');
      ppBtn.classList.remove('is-fading');
      ppBtn.setAttribute('aria-label', L_PLAY);
      if (fadeTimer) { clearTimeout(fadeTimer); fadeTimer = null; }
    }

    function showPauseThenFade() {
      if (iconPlay) iconPlay.style.display = 'none';
      if (iconPause) iconPause.style.display = '';
      ppBtn.classList.add('is-visible');
      ppBtn.classList.add('is-fading');
      ppBtn.setAttribute('aria-label', L_PAUSE);
      if (fadeTimer) clearTimeout(fadeTimer);
      fadeTimer = setTimeout(function () {
        ppBtn.classList.remove('is-visible', 'is-fading');
        fadeTimer = null;
      }, 800);
    }

    function togglePlay() {
      if (player.paused) {
        var p = player.play();
        if (p && typeof p.catch === 'function') {
          p.catch(function () {
            /* Autoplay / unmuted-play blocked: retry muted so motion keeps
               the visual brief even when the browser forbids sound. */
            player.muted = true;
            syncMute();
            player.play().catch(function () {});
          });
        }
      } else {
        player.pause();
      }
    }

    muteBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      player.muted = !player.muted;
      syncMute();
    });

    ppBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      togglePlay();
    });

    player.addEventListener('click', togglePlay);

    /* Let the <video> broadcast the true state — covers autoplay, user
       gesture resumes, and any programmatic play/pause we miss. */
    player.addEventListener('play', showPauseThenFade);
    player.addEventListener('pause', showPlayPersistent);

    syncMute();

    if (autoplayFlag) {
      /* Video starts muted + autoplay via HTML attrs; if the browser
         blocked it, fall back to showing the play pill so the user can
         start it themselves. */
      requestAnimationFrame(function () {
        if (player.paused) showPlayPersistent();
        else showPauseThenFade();
      });
    } else {
      showPlayPersistent();
    }

    /* Offscreen pause — autoplay direct-URL videos should stop streaming
       when the user scrolls them out of view. Saves bandwidth + CPU on
       long pages with multiple ambient videos; resumes automatically on
       scroll back. Gated to autoplay mode so user-initiated playback
       never pauses behind their back. */
    if (autoplayFlag && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            if (wasPlayingBeforeOffscreen && player.paused) {
              var p = player.play();
              if (p && typeof p.catch === 'function') p.catch(function () {});
            }
          } else if (!player.paused) {
            wasPlayingBeforeOffscreen = true;
            player.pause();
          }
        });
      }, { rootMargin: '150px 0px', threshold: 0 });
      io.observe(wrapper);
    }
  }

  function initAllVideoInline(scope) {
    (scope || document).querySelectorAll('[data-video-inline]').forEach(initVideoInline);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAllVideoInline(); });
  } else {
    initAllVideoInline();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    if (ev.target) initAllVideoInline(ev.target);
  });
})();

/* -------------------------------------------------------------------------- */
/* Collection grid: filter sheet + sort + ajax pagination + tab nav            */
/* -------------------------------------------------------------------------- */

/* Drives the collection-page UX (toolbar, sort dropdown, filter drawer,
   active filter pills, ajax pagination, category tab nav) shared by
   `sections/collection.liquid` AND `sections/collection-grid.liquid` via
   the `[data-collection-grid]` selector. Lifted out of collection-grid's
   `{% javascript %}` block when the toolbar/grid markup was extracted into
   `snippets/collection-products.liquid` so both sections could reuse it
   without duplicating ~350 lines of JS or scoping it to one section. */

(function () {
  'use strict';

  var delegated = false;

  function getSectionKey(root) {
    return root.getAttribute('data-section-api-id');
  }

  function stripSectionsParam(urlString) {
    var u = new URL(urlString, window.location.origin);
    u.searchParams.delete('sections');
    return u.pathname + u.search;
  }

  /* Release the scroll lock + restore focus before the AJAX swap destroys
     the open drawer. The replacement comes back server-rendered closed
     (no `.is-open`, `inert`, `aria-hidden="true"`), which means the
     `closeSheet()` call chained after the swap short-circuits on its
     `.is-open` check and the lock + focus stash leak. Symptom: page
     becomes unscrollable after the first filter pick on mobile. Capture
     state BEFORE swap so we can release deterministically. SerenScrollLock
     is ref-counted, so the unlock balances the openSheet() lock that
     happened earlier. */
  function releaseDrawerLockIfOpen(root) {
    var sheet = root.querySelector('[data-collection-filter-sheet]');
    if (!sheet || !sheet.classList.contains('is-open')) return;
    var lastFocus = sheet.__serenLastFocus || null;
    if (window.SerenScrollLock) window.SerenScrollLock.unlock();
    else document.documentElement.classList.remove('is-scroll-locked');
    if (
      lastFocus &&
      typeof lastFocus.focus === 'function' &&
      document.contains(lastFocus)
    ) {
      lastFocus.focus();
    }
  }

  function fetchCollectionSection(root, requestUrl, opts) {
    var key = getSectionKey(root);
    if (!key) return Promise.resolve({ ok: false });
    var pushHistory = !!(opts && opts.pushHistory);

    var u = new URL(requestUrl, window.location.origin);
    u.searchParams.set('sections', key);

    return fetch(u.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) {
        if (!res.ok) throw new Error('status ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var html = data[key];
        if (!html || typeof html !== 'string') return { ok: false };
        var wrap = document.createElement('div');
        wrap.innerHTML = html.trim();
        var nextRoot = wrap.querySelector('[data-collection-grid]');
        if (!nextRoot) return { ok: false };
        var nextInner = nextRoot.querySelector('[data-collection-grid-inner]');
        var curInner = root.querySelector('[data-collection-grid-inner]');
        if (!nextInner || !curInner) return { ok: false };
        releaseDrawerLockIfOpen(root);
        curInner.innerHTML = nextInner.innerHTML;
        bindFilterSheet(root);
        var cleanUrl = stripSectionsParam(u.toString());
        if (pushHistory) {
          window.history.pushState({ collectionGrid: true }, '', cleanUrl);
        } else {
          window.history.replaceState({}, '', cleanUrl);
        }
        return { ok: true };
      });
  }

  function openSheet(root) {
    var sheet = root.querySelector('[data-collection-filter-sheet]');
    if (!sheet || sheet.classList.contains('is-open')) return;
    var toggle = root.querySelector('[data-collection-filter-open]');
    /* Stash the trigger so closeSheet can restore focus before flipping
       aria-hidden — Chrome warns when aria-hidden is set on an ancestor
       of the active element (e.g. the apply button after Enter). Pinned
       to the sheet element since multiple section roots share this code. */
    sheet.__serenLastFocus =
      (document.activeElement && document.activeElement !== document.body
        ? document.activeElement
        : toggle) || null;
    sheet.classList.add('is-open');
    sheet.setAttribute('aria-hidden', 'false');
    sheet.inert = false;
    if (window.SerenScrollLock) window.SerenScrollLock.lock();
    else document.documentElement.classList.add('is-scroll-locked');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
  }

  function closeSheet(root) {
    var sheet = root.querySelector('[data-collection-filter-sheet]');
    if (!sheet || !sheet.classList.contains('is-open')) return;
    sheet.classList.remove('is-open');
    var lastFocus = sheet.__serenLastFocus;
    if (
      lastFocus &&
      typeof lastFocus.focus === 'function' &&
      document.contains(lastFocus)
    ) {
      lastFocus.focus();
    } else if (
      document.activeElement &&
      sheet.contains(document.activeElement) &&
      typeof document.activeElement.blur === 'function'
    ) {
      document.activeElement.blur();
    }
    sheet.__serenLastFocus = null;
    sheet.setAttribute('aria-hidden', 'true');
    sheet.inert = true;
    if (window.SerenScrollLock) window.SerenScrollLock.unlock();
    else document.documentElement.classList.remove('is-scroll-locked');
    var toggle = root.querySelector('[data-collection-filter-open]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function bindTablistScrollHints(root) {
    var wrap = root.querySelector('[data-collection-grid-tablist-wrap]');
    if (!wrap || wrap.getAttribute('data-tablist-hint-bound') === 'true') return;
    var list = wrap.querySelector('[data-collection-grid-tablist]');
    if (!list) return;
    wrap.setAttribute('data-tablist-hint-bound', 'true');

    function update() {
      var maxScrollPx = list.scrollWidth - list.clientWidth;
      if (maxScrollPx < 2) {
        wrap.classList.remove('is-scrollable', 'is-at-start', 'is-at-end');
        return;
      }
      wrap.classList.add('is-scrollable');
      var sl = list.scrollLeft;
      wrap.classList.toggle('is-at-start', sl <= 2);
      wrap.classList.toggle('is-at-end', sl >= maxScrollPx - 2);
    }

    list.addEventListener('scroll', update, { passive: true });
    if (typeof ResizeObserver === 'function') {
      var ro = new ResizeObserver(update);
      ro.observe(list);
    } else {
      window.addEventListener('resize', update);
    }
    update();
  }

  function clearTransitioning(root) {
    var inner = root.querySelector('[data-collection-grid-inner]');
    if (!inner) return;
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        inner.classList.remove('is-transitioning');
      });
    });
  }

  function fetchCollectionPage(root, requestUrl, opts) {
    var pushHistory = !!(opts && opts.pushHistory);
    return fetch(requestUrl, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('status ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var nextRoot = doc.querySelector('[data-collection-grid]');
        if (!nextRoot) return { ok: false };
        var nextInner = nextRoot.querySelector('[data-collection-grid-inner]');
        var curInner = root.querySelector('[data-collection-grid-inner]');
        if (!nextInner || !curInner) return { ok: false };
        releaseDrawerLockIfOpen(root);
        curInner.innerHTML = nextInner.innerHTML;
        var nextTitle = doc.querySelector('title');
        if (nextTitle) document.title = nextTitle.textContent;
        bindFilterSheet(root);
        if (pushHistory) {
          window.history.pushState({ collectionGrid: true }, '', requestUrl);
        } else {
          window.history.replaceState({}, '', requestUrl);
        }
        return { ok: true };
      });
  }

  function fetchAndSwap(root, requestUrl, opts) {
    return fetchCollectionSection(root, requestUrl, opts)
      .then(function (result) {
        if (result && result.ok) return result;
        return fetchCollectionPage(root, requestUrl, opts);
      })
      .catch(function () {
        return fetchCollectionPage(root, requestUrl, opts).catch(function () {
          return { ok: false };
        });
      })
      .then(function (result) {
        // Both transport paths (Section Rendering API + full-page fetch) failed.
        // Surface a toast so the filter/sort UI doesn't appear to freeze silently.
        if (!result || !result.ok) {
          var msg = window.t ? window.t('collection_load_error') : '';
          if (msg && window.SerenToast && typeof window.SerenToast.error === 'function') {
            window.SerenToast.error(msg);
          }
        }
        return result;
      });
  }

  function animateTabNav(root, url, opts) {
    var pushHistory = !(opts && opts.pushHistory === false);
    var inner = root.querySelector('[data-collection-grid-inner]');
    if (!inner || prefersReducedMotion()) {
      return fetchAndSwap(root, url, { pushHistory: pushHistory });
    }
    inner.classList.add('is-transitioning');
    var fadeWait = new Promise(function (r) {
      window.setTimeout(r, 180);
    });
    var fetchP = fetchAndSwap(root, url, { pushHistory: pushHistory });
    return Promise.all([fadeWait, fetchP]).then(function () {
      clearTransitioning(root);
    });
  }

  function bindFilterSheet(root) {
    var sheet = root.querySelector('[data-collection-filter-sheet]');
    if (sheet && sheet.getAttribute('data-sheet-bound') !== 'true') {
      sheet.setAttribute('data-sheet-bound', 'true');
      var openBtn = root.querySelector('[data-collection-filter-open]');
      var backdrop = root.querySelector('[data-collection-filter-backdrop]');
      if (openBtn) {
        openBtn.addEventListener('click', function () {
          openSheet(root);
        });
      }
      root.querySelectorAll('[data-collection-filter-close]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          closeSheet(root);
        });
      });
      if (backdrop) {
        backdrop.addEventListener('click', function () {
          closeSheet(root);
        });
      }
    }
    bindTablistScrollHints(root);
  }

  function delegateCollectionGrid() {
    if (delegated) return;
    delegated = true;

    document.addEventListener('click', function (e) {
      var root = e.target.closest('[data-collection-grid]');
      if (!root) return;
      var a = e.target.closest('a[data-collection-ajax-link]');
      if (!a || !root.contains(a)) return;
      if (e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (a.getAttribute('href') == null || a.getAttribute('href') === '') return;
      var isPagination = !!a.closest('.pagination');
      e.preventDefault();
      fetchAndSwap(root, a.href).then(function () {
        closeSheet(root);
        if (!isPagination) return;
        var anchor = root.querySelector('.collection-grid__toolbar') || root;
        var rect = anchor.getBoundingClientRect();
        if (rect.top >= 0 && rect.top < window.innerHeight) return;
        anchor.scrollIntoView({
          block: 'start',
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
      });
    });

    document.addEventListener('click', function (e) {
      var tab = e.target.closest('a[data-collection-tab-link]');
      if (!tab) return;
      var root = tab.closest('[data-collection-grid]');
      if (!root) return;
      if (e.ctrlKey || e.metaKey || e.shiftKey) return;
      var href = tab.getAttribute('href');
      if (!href) return;
      e.preventDefault();
      root.querySelectorAll('[data-collection-tab-link]').forEach(function (t) {
        if (t === tab) {
          t.setAttribute('aria-current', 'page');
        } else {
          t.removeAttribute('aria-current');
        }
      });
      animateTabNav(root, href);
    });

    window.addEventListener('popstate', function (e) {
      if (!e.state || !e.state.collectionGrid) return;
      var root = document.querySelector('[data-collection-grid]');
      if (!root) return;
      animateTabNav(root, window.location.href, { pushHistory: false });
    });

    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form.collection-filters__price');
      if (!form) return;
      var root = form.closest('[data-collection-grid]');
      if (!root) return;
      e.preventDefault();
      var fu = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
      fu.search = '';
      var fd = new FormData(form);
      fd.forEach(function (v, k) {
        if (v !== '' && v != null) {
          fu.searchParams.append(k, v);
        }
      });
      fetchAndSwap(root, fu.toString()).then(function () {
        closeSheet(root);
      });
    });

    document.addEventListener('change', function (e) {
      var sel = e.target.closest('[data-collection-sort-select]');
      if (!sel) return;
      var root = sel.closest('[data-collection-grid]');
      if (!root) return;
      var form = root.querySelector('[data-collection-sort-form]');
      if (!form) return;
      var u = new URL(form.getAttribute('action'), window.location.origin);
      u.search = '';
      var fd = new FormData(form);
      fd.forEach(function (v, k) {
        if (k === 'sort_by') return;
        if (v !== '' && v != null) {
          u.searchParams.append(k, v);
        }
      });
      u.searchParams.set('sort_by', sel.value);
      fetchAndSwap(root, u.toString()).catch(function () {});
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      document.querySelectorAll('[data-collection-filter-sheet].is-open').forEach(function (sheet) {
        var root = sheet.closest('[data-collection-grid]');
        if (root) closeSheet(root);
      });
    });
  }

  function initCollectionSheets() {
    document.querySelectorAll('[data-collection-grid]').forEach(bindFilterSheet);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      delegateCollectionGrid();
      initCollectionSheets();
    });
  } else {
    delegateCollectionGrid();
    initCollectionSheets();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    delegateCollectionGrid();
    if (ev.target) {
      ev.target.querySelectorAll('[data-collection-grid]').forEach(bindFilterSheet);
    }
  });
})();

/* -------------------------------------------------------------------------- */
/* Collection showcase: tabbed product rails                                   */
/* -------------------------------------------------------------------------- */

/* Drives `sections/collection-showcase.liquid` — tab list + crossfade panel
   transitions + horizontal scroll-snap rail with custom scrollbar thumb +
   keyboard tab nav (arrow keys / Home / End). Lifted out of the section's
   `{% javascript %}` block to match the SerenCollectionGrid pattern; same
   selectors + behavior so the section markup stays untouched apart from
   the new per-tab description block. No-ops on pages without
   `[data-collection-showcase-tabs]`. */

(function () {
  'use strict';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function bindShowcaseRail(root) {
    if (!root) return;
    var viewport = root.querySelector('[data-showcase-viewport]');
    var thumb = root.querySelector('[data-showcase-thumb]');
    var track = root.querySelector('[data-showcase-track]');
    if (!viewport || !thumb || !track) return;

    function maxScroll() {
      return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    }

    function updateThumb() {
      var ms = maxScroll();
      var cw = viewport.clientWidth;
      var sw = viewport.scrollWidth;
      if (ms < 1 || sw <= cw) {
        thumb.style.width = '100%';
        thumb.style.left = '0%';
        thumb.classList.add('is-full');
        return;
      }
      thumb.classList.remove('is-full');
      var thumbPct = Math.min(100, Math.max(18, (cw / sw) * 100));
      var travelPct = 100 - thumbPct;
      var leftPct = travelPct > 0 ? (viewport.scrollLeft / ms) * travelPct : 0;
      thumb.style.width = thumbPct + '%';
      thumb.style.left = leftPct + '%';
    }

    root.__serenShowcaseUpdateThumb = updateThumb;

    if (root.getAttribute('data-showcase-rail-bound') === 'true') {
      updateThumb();
      return;
    }
    root.setAttribute('data-showcase-rail-bound', 'true');

    viewport.addEventListener('scroll', updateThumb, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(updateThumb);
      ro.observe(viewport);
    } else {
      window.addEventListener('resize', updateThumb);
    }

    updateThumb();

    track.addEventListener('click', function (e) {
      if (e.target !== track) return;
      var ms = maxScroll();
      if (ms < 1) return;
      var rect = track.getBoundingClientRect();
      var x = e.clientX - rect.left;
      var ratio = x / rect.width;
      var target = ratio * ms;
      viewport.scrollTo({
        left: target,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    });

    thumb.addEventListener('pointerdown', function (e) {
      if (e.button !== 0) return;
      e.preventDefault();
      var startX = e.clientX;
      var startScroll = viewport.scrollLeft;
      function move(ev) {
        var ms2 = maxScroll();
        if (ms2 < 1) return;
        var trackW = track.clientWidth;
        var thumbW = thumb.offsetWidth;
        var travel = Math.max(1, trackW - thumbW);
        var dx = ev.clientX - startX;
        var next = startScroll + (dx / travel) * ms2;
        viewport.scrollLeft = Math.max(0, Math.min(ms2, next));
      }
      function up() {
        document.removeEventListener('pointermove', move);
        document.removeEventListener('pointerup', up);
      }
      document.addEventListener('pointermove', move);
      document.addEventListener('pointerup', up);
    });
  }

  function initShowcaseRails(ctx) {
    var scope = ctx && ctx.querySelectorAll ? ctx : document;
    var roots = scope.querySelectorAll ? scope.querySelectorAll('[data-collection-showcase]') : [];
    roots.forEach(bindShowcaseRail);
  }

  function activateShowcaseTab(root, index) {
    var tabs = root.querySelectorAll('.tablist__tab[role="tab"]');
    var panels = root.querySelectorAll('[data-tab-panel]');
    if (panels.length === 0) return;

    tabs.forEach(function (tab, i) {
      var on = i === index;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
    });

    var activeTab = tabs[index];
    if (activeTab && activeTab.scrollIntoView) {
      activeTab.scrollIntoView({
        inline: 'nearest',
        block: 'nearest',
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }

    panels.forEach(function (panel, i) {
      var on = i === index;
      if (on) {
        panel.classList.add('is-active');
        panel.setAttribute('aria-hidden', 'false');
        panel.removeAttribute('inert');
      } else {
        panel.classList.remove('is-active');
        panel.setAttribute('aria-hidden', 'true');
        panel.setAttribute('inert', '');
      }
    });

    var activePanel = panels[index];
    if (activePanel) {
      var vp = activePanel.querySelector('[data-showcase-viewport]');
      if (vp) {
        vp.scrollLeft = 0;
      }
      window.requestAnimationFrame(function () {
        initShowcaseRails(activePanel);
        var rail = activePanel.querySelector('[data-collection-showcase]');
        if (rail && typeof rail.__serenShowcaseUpdateThumb === 'function') {
          rail.__serenShowcaseUpdateThumb();
        }
      });
    }
  }

  function bindTablistScrollHints(wrap) {
    if (!wrap || wrap.getAttribute('data-tablist-hint-bound') === 'true') return;
    var list = wrap.querySelector('.tablist');
    if (!list) return;
    wrap.setAttribute('data-tablist-hint-bound', 'true');

    function updateHints() {
      var maxScrollPx = list.scrollWidth - list.clientWidth;
      if (maxScrollPx < 2) {
        wrap.classList.remove('is-scrollable', 'is-at-start', 'is-at-end');
        return;
      }
      wrap.classList.add('is-scrollable');
      var sl = list.scrollLeft;
      wrap.classList.toggle('is-at-start', sl <= 2);
      wrap.classList.toggle('is-at-end', sl >= maxScrollPx - 2);
    }

    list.addEventListener('scroll', updateHints, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      var roHint = new ResizeObserver(updateHints);
      roHint.observe(list);
    } else {
      window.addEventListener('resize', updateHints);
    }
    updateHints();
  }

  function bindCollectionShowcaseTabs(root) {
    if (!root || root.getAttribute('data-collection-showcase-tabs-init') === 'true') return;
    root.setAttribute('data-collection-showcase-tabs-init', 'true');

    var tabs = root.querySelectorAll('.tablist__tab[role="tab"]');
    if (tabs.length === 0) {
      initShowcaseRails(root);
      return;
    }

    root.addEventListener('click', function (e) {
      var tab = e.target.closest('.tablist__tab[role="tab"]');
      if (!tab || !root.contains(tab)) return;
      var idx = parseInt(tab.getAttribute('data-tab-index'), 10);
      if (isNaN(idx)) return;
      activateShowcaseTab(root, idx);
      tab.focus();
    });

    root.addEventListener('keydown', function (e) {
      var tabEls = Array.prototype.slice.call(root.querySelectorAll('.tablist__tab[role="tab"]'));
      if (tabEls.length === 0) return;
      var current = document.activeElement;
      var ix = tabEls.indexOf(current);
      if (ix < 0) return;

      var next = ix;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        next = (ix + 1) % tabEls.length;
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        next = (ix - 1 + tabEls.length) % tabEls.length;
      } else if (e.key === 'Home') {
        e.preventDefault();
        next = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        next = tabEls.length - 1;
      } else {
        return;
      }
      activateShowcaseTab(root, next);
      tabEls[next].focus();
    });

    var tablistWrap = root.querySelector('[data-collection-showcase-tablist-wrap]');
    if (tablistWrap) {
      bindTablistScrollHints(tablistWrap);
    }

    initShowcaseRails(root);
  }

  function initAllTabs() {
    document.querySelectorAll('[data-collection-showcase-tabs]').forEach(bindCollectionShowcaseTabs);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllTabs);
  } else {
    initAllTabs();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    if (ev.target && ev.target.querySelectorAll) {
      ev.target.querySelectorAll('[data-collection-showcase-tabs]').forEach(bindCollectionShowcaseTabs);
    }
  });
})();

/* ──────────────────────────────────────────────────────────────
   SerenVideoModal — global video lightbox
   Triggered by any element with [data-video-modal] attribute,
   opens the modal markup rendered by snippets/video-modal.liquid
   in layout/theme.liquid. Moved here from inline <script> for perf
   (one less inline parse pass, one fewer HTTP payload bump per page).
   ────────────────────────────────────────────────────────────── */
(function () {
  'use strict';
  if (window.__serenVideoModal) return;
  window.__serenVideoModal = true;

  var root = document.querySelector('[data-video-modal-root]');
  if (!root) return;

  var player = root.querySelector('[data-video-modal-player]');
  var muteBtn = root.querySelector('[data-video-modal-mute]');
  var ppBtn = root.querySelector('[data-video-modal-playpause]');
  var ppPlay = root.querySelector('.video-modal__pp-play');
  var ppPause = root.querySelector('.video-modal__pp-pause');
  var iconMuted = root.querySelector('.video-modal__icon-muted');
  var iconUnmuted = root.querySelector('.video-modal__icon-unmuted');
  var captionEl = root.querySelector('[data-video-modal-caption]');
  var creditEl = root.querySelector('[data-video-modal-credit]');
  var defaultAriaLabel = root.getAttribute('aria-label');
  var OPEN_CLASS = 'is-open';
  var BODY_LOCK = 'is-scroll-locked';
  var prevFocus = null;
  var ppFadeTimer = null;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Locale strings (window.t reads #theme-strings) so aria-labels stay in
     sync with the merchant's active locale. */
  var L_MUTE = window.t('video_mute', 'Mute');
  var L_UNMUTE = window.t('video_unmute', 'Unmute');
  var L_PLAY = window.t('video_play', 'Play video');
  var L_PAUSE = window.t('video_pause', 'Pause video');

  /* Toggle buttons here say the ACTION in their label ("Unmute" / "Pause
     video") and carry no aria-pressed — one state signal, not two. */
  function syncMuteUI() {
    var muted = player.muted;
    muteBtn.setAttribute('aria-label', muted ? L_UNMUTE : L_MUTE);
    iconMuted.style.display = muted ? '' : 'none';
    iconUnmuted.style.display = muted ? 'none' : '';
  }

  function syncPlayPauseUI(paused) {
    if (paused) {
      ppPlay.style.display = '';
      ppPause.style.display = 'none';
      ppBtn.classList.add('is-visible');
      ppBtn.classList.remove('is-fading');
      if (ppFadeTimer) { clearTimeout(ppFadeTimer); ppFadeTimer = null; }
    } else {
      ppPlay.style.display = 'none';
      ppPause.style.display = '';
      ppBtn.classList.add('is-visible');
      ppBtn.classList.add('is-fading');
      if (ppFadeTimer) clearTimeout(ppFadeTimer);
      ppFadeTimer = setTimeout(function () {
        ppBtn.classList.remove('is-visible');
        ppBtn.classList.remove('is-fading');
        ppFadeTimer = null;
      }, 800);
    }
  }

  function togglePlayPause() {
    if (player.paused) {
      var p = player.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
      syncPlayPauseUI(false);
    } else {
      player.pause();
      syncPlayPauseUI(true);
    }
  }

  /* Captions <track> lifecycle. Created lazily on first use, src swapped
     on each open. Setting track.track.mode = 'showing' synchronously
     covers Chrome/Firefox/Safari; we re-set it on the load event in case
     the TextTrack object isn't ready immediately.

     crossOrigin is toggled HERE (not as a static attribute on the <video>)
     so non-CORS video URLs aren't blocked when captions aren't in use.
     The HTML5 spec requires CORS on the <video> element for cross-origin
     <track> parsing — there's no per-track crossorigin in the spec, so
     we set/unset on the parent based on whether captions are present.
     Every open() runs after a close()'s player.load() reset, so the
     attribute change applies before src is set on the next play. */
  function applyCaptions(captionsUrl) {
    var track = player.querySelector('track[data-video-modal-captions]');
    if (captionsUrl) {
      player.crossOrigin = 'anonymous';
      if (!track) {
        track = document.createElement('track');
        track.setAttribute('data-video-modal-captions', '');
        track.kind = 'captions';
        track.label = 'Captions';
        /* Derive language tag from <html lang>, splitting on `-` so a
           regional code like `en-US` becomes `en` (BCP-47 base subtag).
           Falls back to English if the document lang is missing. */
        track.srclang = (document.documentElement.lang || 'en').split('-')[0];
        track.addEventListener('load', function () {
          if (track.track) track.track.mode = 'showing';
        });
        player.appendChild(track);
      }
      track.src = captionsUrl;
      if (track.track) track.track.mode = 'showing';
    } else {
      player.removeAttribute('crossorigin');
      if (track) {
        if (track.track) track.track.mode = 'disabled';
        track.removeAttribute('src');
      }
    }
  }

  function applyMeta(caption, credit) {
    captionEl.textContent = caption || '';
    creditEl.textContent = credit || '';
    /* Dialog accessible name: prefer caption (specific, on-screen) when
       provided; fall back to the generic localized "Video" label. */
    if (caption) {
      root.setAttribute('aria-label', caption);
    } else if (defaultAriaLabel) {
      root.setAttribute('aria-label', defaultAriaLabel);
    }
  }

  function open(src, posterOrOpts) {
    if (!src) return;
    /* Backwards-compat: pre-redesign callers passed a poster URL string
       as the 2nd arg. New form takes an options object
       { poster, caption, credit, captions }. Detect and normalize so
       any external programmatic caller (apps) keeps working. */
    var opts;
    if (typeof posterOrOpts === 'string') {
      opts = { poster: posterOrOpts };
    } else if (posterOrOpts && typeof posterOrOpts === 'object') {
      opts = posterOrOpts;
    } else {
      opts = {};
    }
    prevFocus = document.activeElement;

    applyMeta(opts.caption, opts.credit);
    applyCaptions(opts.captions);

    player.src = src;
    player.poster = opts.poster || '';
    player.muted = false;
    player.loop = true;
    syncMuteUI();

    root.classList.add(OPEN_CLASS);
    root.setAttribute('aria-hidden', 'false');
    root.inert = false;
    document.documentElement.classList.add(BODY_LOCK);

    var p = player.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () {
        player.muted = true;
        syncMuteUI();
        player.play().catch(function () {});
      });
    }

    requestAnimationFrame(function () {
      root.querySelector('[data-video-modal-close]').focus({ preventScroll: true });
    });
  }

  function close() {
    root.classList.remove(OPEN_CLASS);
    /* Move focus OUT before aria-hidden — Chrome warns when aria-hidden
       is set on an ancestor of the active element. Validate prevFocus
       is still in DOM (a section refresh could detach it) and fall
       back to blurring the inside descendant. */
    if (prevFocus && typeof prevFocus.focus === 'function' && document.contains(prevFocus)) {
      prevFocus.focus({ preventScroll: true });
    } else if (
      document.activeElement &&
      root.contains(document.activeElement) &&
      typeof document.activeElement.blur === 'function'
    ) {
      document.activeElement.blur();
    }
    prevFocus = null;
    root.setAttribute('aria-hidden', 'true');
    root.inert = true;
    document.documentElement.classList.remove(BODY_LOCK);

    applyMeta('', '');
    applyCaptions('');

    player.pause();
    player.removeAttribute('src');
    player.load();
  }

  /* Triggers — any element with [data-video-modal]. The optional
     data-video-caption / data-video-credit / data-video-captions are
     read here and forwarded into the modal's meta + captions track.
     All four are optional; existing triggers without them continue to
     work unchanged. */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-video-modal]');
    if (!trigger) return;
    e.preventDefault();
    var src = trigger.getAttribute('data-video-src');
    open(src, {
      poster: trigger.getAttribute('data-video-poster') || '',
      caption: trigger.getAttribute('data-video-caption') || '',
      credit: trigger.getAttribute('data-video-credit') || '',
      captions: trigger.getAttribute('data-video-captions') || ''
    });
  });

  /* Close handlers */
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-video-modal-close]')) {
      e.preventDefault();
      close();
    }
  });

  /* ESC */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && root.classList.contains(OPEN_CLASS)) {
      close();
    }
  });

  /* Mute toggle */
  muteBtn.addEventListener('click', function (e) {
    e.preventDefault();
    player.muted = !player.muted;
    syncMuteUI();
  });

  /* Play/pause — click the video or the bottom-right pill. The pill's label
     follows the player's real state (autoplay on open included). */
  player.addEventListener('play', function () {
    ppBtn.setAttribute('aria-label', L_PAUSE);
  });
  player.addEventListener('pause', function () {
    ppBtn.setAttribute('aria-label', L_PLAY);
  });
  player.addEventListener('click', togglePlayPause);
  ppBtn.addEventListener('click', function (e) {
    e.preventDefault();
    togglePlayPause();
  });

  /* Focus trap */
  root.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var focusable = root.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* Public API for programmatic use */
  window.SerenVideoModal = { open: open, close: close };
})();

/* ============================================================
 * Drag-to-scroll — desktop click-and-drag for horizontal carousels.
 * Opt in with [data-drag-scroll] on the overflow-x scroll container. Mouse/pen
 * only (touch keeps native scrolling); an 8px threshold + horizontal intent so it
 * never hijacks a click or a vertical scroll, and a committed drag swallows the
 * trailing click so a link/card inside doesn't navigate. Composes with existing
 * arrows / edge-fades / scroll-snap — it only sets scrollLeft. Mirrors the gallery
 * drag in product-gallery.js. Re-inits on shopify:section:load (theme editor); a
 * ResizeObserver keeps the grab cursor in sync as content (e.g. a lazy IG feed)
 * makes the track overflow.
 * ============================================================ */
(function () {
  var FINE = window.matchMedia ? window.matchMedia('(pointer: fine)') : null;
  var INTERACTIVE = 'a, button, input, select, textarea, label, summary, video, iframe';

  function initDragScroll(el) {
    if (!el || el.getAttribute('data-drag-scroll-init') === 'true') return;
    el.setAttribute('data-drag-scroll-init', 'true');

    var down = false, committed = false, pid = null;
    var startX = 0, startY = 0, startScroll = 0, suppressClick = false;

    function updateReady() {
      el.classList.toggle('is-drag-ready', el.scrollWidth > el.clientWidth + 1);
    }
    updateReady();
    if (window.ResizeObserver) {
      new ResizeObserver(updateReady).observe(el);
    } else {
      window.addEventListener('resize', updateReady);
    }

    el.addEventListener('dragstart', function (e) { e.preventDefault(); }, true);

    el.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' && e.pointerType !== 'pen') return;
      if (e.button !== 0) return;
      if (e.target && e.target.closest && e.target.closest(INTERACTIVE)) return;
      if (el.scrollWidth <= el.clientWidth) return; // nothing to scroll
      down = true; committed = false; pid = e.pointerId;
      startX = e.clientX; startY = e.clientY; startScroll = el.scrollLeft;
      suppressClick = false;
    });

    el.addEventListener('pointermove', function (e) {
      if (!down || e.pointerId !== pid) return;
      var dx = e.clientX - startX, dy = e.clientY - startY;
      if (!committed) {
        if (Math.abs(dx) < 8) return;
        if (Math.abs(dx) <= Math.abs(dy)) return; // leave vertical gestures alone
        committed = true;
        el.classList.add('is-drag-scrolling');
        el.style.scrollSnapType = 'none';
        try { el.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      }
      el.scrollLeft = startScroll - dx;
    });

    function end(e) {
      if (!down) return;
      if (e && e.pointerId !== pid) return;
      down = false;
      var wasCommitted = committed; committed = false;
      var p = pid; pid = null;
      el.classList.remove('is-drag-scrolling');
      el.style.scrollSnapType = '';
      try { if (p != null && el.hasPointerCapture(p)) el.releasePointerCapture(p); } catch (err) { /* ignore */ }
      if (wasCommitted) suppressClick = true;
    }
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
    el.addEventListener('lostpointercapture', end);

    el.addEventListener('click', function (e) {
      if (suppressClick) { e.preventDefault(); e.stopPropagation(); suppressClick = false; }
    }, true);
  }

  function initAll(root) {
    if (!FINE || !FINE.matches) return; // desktop pointer only
    (root || document).querySelectorAll('[data-drag-scroll]').forEach(initDragScroll);
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', function () { initAll(); });
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target || document); });
})();

